import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import storageService, { FileItem } from '../services/storageService';
import { toPublicAssetUrl } from '../utils/publicAssetUrl';
import AutoTranslateText from './AutoTranslateText';
import '@google/model-viewer';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import axios from 'axios';
import { generateAIMedia } from '../services/aiService';
import { apiKeyService } from '../services/apiKeyService';

// ── Tâches disponibles dans AI Studio ──────────────────────────────────────
const STUDIO_TASKS = [
  { id: 'create-image',        icon: '🖌️',  label: 'Créer\nImage',                color: '#e11d48', soon: false },
  { id: 'create-video',        icon: '🎥',  label: 'Créer\nVidéo',                color: '#7c3aed', soon: false },
  { id: 'image-from-prompt',   icon: '🖼',  label: 'Image depuis\nprompt',        color: '#3b82f6', soon: false },
  { id: 'video-from-prompt',   icon: '🎬',  label: 'Vidéo depuis\nprompt',        color: '#8b5cf6', soon: false },
  { id: 'video-from-image',    icon: '🎞',  label: 'Vidéo depuis\nimage',         color: '#ec4899', soon: false },
  { id: 'video-img-comfy',     icon: '🎞✨', label: 'Image+Texte\nvers Vidéo',     color: '#6366f1', soon: false },
  { id: 'continue-video',      icon: '▶️',  label: 'Continuer\nune vidéo',        color: '#f97316', soon: false },
  { id: 'model3d-from-image',  icon: '🧊',  label: 'Modèle 3D\ndepuis image',    color: '#06b6d4', soon: true  },
  { id: 'ocr',                 icon: '📄',  label: 'Extraire\ntexte (OCR)',       color: '#10b981', soon: false },
  { id: 'tts-video',           icon: '🔊',  label: 'Voix sur\nvidéo',             color: '#f59e0b', soon: false },
  { id: 'subtitles',           icon: '💬',  label: 'Sous-titrage\nvidéo',         color: '#6366f1', soon: false },
  { id: 'edit-image',          icon: '🖌',  label: 'Éditer\nune image',           color: '#ef4444', soon: false },
  { id: 'edit-video',          icon: '✂️',  label: 'Éditer\nune vidéo',           color: '#84cc16', soon: false },
  { id: 'edit-audio',          icon: '🎧',  label: 'Éditer\nun audio',             color: '#06b6d4', soon: false },
] as const;
import ImageEditor from './ImageEditor';
import VideoEditor from './VideoEditor';
import AudioEditor from './AudioEditor';

// Déclaration pour TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': any;
    }
  }
}

// ---------------------------------------------------------------------------
// 1. COMPOSANTS UTILITAIRES (SafeImage, Viewer 3D, Editeur)
// ---------------------------------------------------------------------------

// ✅ SAFE IMAGE - avec fallback proxy S3
const SafeImage = ({ file, style, className, onClick }: { file: FileItem, style?: React.CSSProperties, className?: string, onClick?: () => void }) => {
    const [src, setSrc] = useState(file.url);
    const [hasError, setHasError] = useState(false);
    const [usedProxy, setUsedProxy] = useState(false);

    useEffect(() => {
        setSrc(file.url);
        setHasError(false);
        setUsedProxy(false);
    }, [file.url]);

    const handleError = () => {
        const isRemote = /^https?:\/\//i.test(file.url);
        if (isRemote && !usedProxy) {
            setUsedProxy(true);
            setSrc(`/api/storage/proxy-image?url=${encodeURIComponent(file.url)}`);
        } else {
            setHasError(true);
        }
    };

    if (hasError) {
        return (
            <div style={style} className={`d-flex align-items-center justify-content-center bg-light text-secondary ${className || ''}`} onClick={onClick}>
                <div className="text-center">
                   <i className="fas fa-image fa-2x mb-1"></i>
                   <div style={{fontSize: '0.6rem'}} className="text-truncate px-1">{file.name}</div>
                </div>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={file.name}
            style={style}
            className={className}
            onError={handleError}
            onClick={onClick}
            loading="lazy"
            referrerPolicy="no-referrer"
        />
    );
};

// Viewer 3D basique (Placeholder)
const FbxViewer: React.FC<{ url: string; height?: number }> = ({ url, height = 250 }) => {
    const mountRef = useRef<HTMLDivElement>(null);
  
    useEffect(() => {
      if (!mountRef.current) return;
      
      const width = mountRef.current.clientWidth;
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf8f9fa);
      
      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      camera.position.set(3, 3, 5);
      
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      mountRef.current.appendChild(renderer.domElement);
      
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);
      
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(5, 10, 7.5);
      scene.add(dirLight);
      
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 2.0;
      
      const loader = new FBXLoader();
      loader.load(url, (object) => {
        const box = new THREE.Box3().setFromObject(object);
        const size = new THREE.Vector3();
        box.getSize(size);
        const center = new THREE.Vector3();
        box.getCenter(center);
        object.position.sub(center);
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.5 / maxDim;
        object.scale.setScalar(scale);
        scene.add(object);
      }, undefined, (error) => {
        console.error('Erreur chargement FBX:', error);
      });
      
      let animationId: number;
      const animate = () => {
        animationId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();
      
      return () => {
        cancelAnimationFrame(animationId);
        if (mountRef.current && renderer.domElement) {
          mountRef.current.removeChild(renderer.domElement);
        }
        renderer.dispose();
      };
    }, [url, height]);
    
    return <div ref={mountRef} style={{ width: '100%', height: height, borderRadius: '4px', overflow: 'hidden' }} />;
};

// --- COMPOSANT PRINCIPAL ---

interface Props {
  onSelect?: (url: string) => void;
  onClose?: () => void;
  multiselect?: boolean;
  isOverlay?: boolean;
  // ✅ AJOUT : Prop pour le contexte de génération (fourni par BookEditor)
  generationContext?: {
    target: 'header' | 'footer' | 'body';
    width?: number;
    height?: number;
  };
}

const CloudFileManager: React.FC<Props> = ({ onSelect, onClose, multiselect = false, isOverlay = !!onClose, generationContext }) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  const [isRenamingPreview, setIsRenamingPreview] = useState(false);
  const [previewRenameValue, setPreviewRenameValue] = useState('');
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [isEditingVideo, setIsEditingVideo] = useState(false);
  const [isEditingAudio, setIsEditingAudio] = useState(false);
  const [isCreatingImage, setIsCreatingImage] = useState(false);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  
  // ✅ AJOUT : Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // ✅ NOUVEAUX ÉTATS POUR L'IA (Mise à jour)
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isUploadingPromptFiles, setIsUploadingPromptFiles] = useState(false);
  // ✅ AJOUT : État pour la barre de progression (Sora)
  const [aiProgress, setAiProgress] = useState<number | null>(null);
  const [ltxDuration, setLtxDuration] = useState(8);
  const [ltxFps, setLtxFps] = useState(25);
  const [ltxResolution, setLtxResolution] = useState('1920x1080');
  const [ltxGenerateAudio, setLtxGenerateAudio] = useState(true);
  const [ltxRetakeStartTime, setLtxRetakeStartTime] = useState(0);
  const [ltxRetakeDuration, setLtxRetakeDuration] = useState(6);
  const [ltxRetakeMode, setLtxRetakeMode] = useState('replace_audio_and_video');
  const [ltxUseUpload, setLtxUseUpload] = useState(true);
  const [ltxPreset, setLtxPreset] = useState<'auto' | 'custom'>('auto');
  const [contextMediaType, setContextMediaType] = useState<'image' | 'video'>('image');
  const [ltxSourceMeta, setLtxSourceMeta] = useState<{ type: 'image' | 'video'; width?: number; height?: number; duration?: number } | null>(null);
  const [ltxCreditsBlocked, setLtxCreditsBlocked] = useState(false);
  const [ltxCreditsMessage, setLtxCreditsMessage] = useState<string | null>(null);

  // ── Seedance 2.0 video params ─────────────────────────────────────────────
  const [comfyDuration, setComfyDuration] = useState(5);
  const [comfySeed, setComfySeed] = useState(() => Math.floor(Math.random() * 2 ** 32));
  const [comfyRandomizeSeed, setComfyRandomizeSeed] = useState(true);
  const [comfyResolution, setComfyResolution] = useState<'480p' | '720p'>('720p');
  const [comfyAspectRatio, setComfyAspectRatio] = useState('16:9');
  // legacy — conservés pour ne pas casser d'autres refs éventuelles
  const [comfyNegativePrompt] = useState('');
  const [comfyFps] = useState(16);
  const [comfySteps] = useState(30);
  const [comfyGuidanceHigh] = useState(6);
  const [comfyGuidanceLow] = useState(1);
  const [comfyScheduler] = useState('UniPCMultistep');
  const [comfyFlowShift] = useState(3);
  const [promptFilePickerOpen, setPromptFilePickerOpen] = useState(false);

  const getPublicSelectUrl = (url: string) => {
    const normalized = toPublicAssetUrl(url);
    return normalized || url;
  };
  const [selectedPromptFiles, setSelectedPromptFiles] = useState<FileItem[]>([]);
  const [availableImageModels, setAvailableImageModels] = useState<Array<{ id: string; label: string; provider: 'OpenAI' | 'GoogleAI' | 'LTX' | 'FalAI' }>>([]);
  const [availableVideoModels, setAvailableVideoModels] = useState<Array<{ id: string; label: string; provider: 'OpenAI' | 'GoogleAI' | 'LTX' | 'FalAI' }>>([]);
  const [selectedImageModel, setSelectedImageModel] = useState('');
  const [selectedVideoModel, setSelectedVideoModel] = useState('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Record<string, { file: FileItem; path: string }>>({});
  const [bulkActionMode, setBulkActionMode] = useState<'move' | 'copy' | null>(null);
  const [bulkActionPath, setBulkActionPath] = useState('');
  const [bulkActionFolders, setBulkActionFolders] = useState<FileItem[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // ✅ NOUVEAU : Choix du type de média en mode création
  const [creationMediaType, setCreationMediaType] = useState<'image' | 'video'>('image');

  // ── AI Studio : tâche sélectionnée + états OCR ──────────────────────────
  const [studioTask, setStudioTask] = useState<string | null>(null);
  const [ocrSourceFile, setOcrSourceFile] = useState<FileItem | null>(null);
  const [ocrResult, setOcrResult] = useState<string | null>(null);
  const [isRunningOcr, setIsRunningOcr] = useState(false);
  // TTS-Video
  const [ttsVideoFile, setTtsVideoFile] = useState<FileItem | null>(null);
  const [ttsText, setTtsText] = useState('');
  const [ttsVoice, setTtsVoice] = useState('nova');
  const [ttsModelQ, setTtsModelQ] = useState('tts-1');
  const [isRunningTts, setIsRunningTts] = useState(false);
  // Subtitles
  const [subVideoFile, setSubVideoFile] = useState<FileItem | null>(null);
  const [subLanguage, setSubLanguage] = useState('auto');
  const [subHardcode, setSubHardcode] = useState(true);
  const [subFont, setSubFont] = useState('Arial');
  const [subFontSize, setSubFontSize] = useState(24);
  const [subColor, setSubColor] = useState('#ffffff');
  const [subOutline, setSubOutline] = useState('#000000');
  const [subBold, setSubBold] = useState(false);
  const [isRunningSubtitles, setIsRunningSubtitles] = useState(false);
  const [subtitleSrt, setSubtitleSrt] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptLocalInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Dark / light mode ───────────────────────────────────────────────────────
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('homeListDarkMode') === 'true');
  useEffect(() => {
    const h = () => setDarkMode(localStorage.getItem('homeListDarkMode') === 'true');
    window.addEventListener('darkModeChange', h);
    return () => window.removeEventListener('darkModeChange', h);
  }, []);

  // ── CSS injection (Bootstrap overrides scoped to .cfm-dark / .cfm-light) ──────
  useEffect(() => {
    const id = 'cfm-theme-css';
    let s = document.getElementById(id) as HTMLStyleElement | null;
    if (!s) { s = document.createElement('style'); s.id = id; document.head.appendChild(s); }
    s.textContent = `
      .cfm-dark{color:#CBD5E1;background:#0F1117}
      .cfm-dark .card{background:#161C2C!important;border-color:#253048!important;color:#CBD5E1}
      .cfm-dark .card-header{background:#111827!important;border-color:#253048!important;color:#E2EBFF}
      .cfm-dark .card-body{background:#161C2C!important}
      .cfm-dark .bg-white{background:#161C2C!important}
      .cfm-dark .bg-light{background:#1A2235!important}
      .cfm-dark .border{border-color:#253048!important}
      .cfm-dark .border-end{border-color:#253048!important}
      .cfm-dark .border-start{border-color:#253048!important}
      .cfm-dark .border-success{border-color:#10B981!important}
      /* Table: override Bootstrap 5 CSS variables so every cell is themed */
      .cfm-dark .table{
        --bs-table-bg:#0F1117;
        --bs-table-striped-bg:#1A2235;
        --bs-table-hover-bg:rgba(79,70,229,.09);
        --bs-table-active-bg:rgba(79,70,229,.14);
        --bs-table-color:#A8BDD8;
        --bs-table-border-color:#253048;
        color:#A8BDD8;border-color:#253048
      }
      .cfm-dark .table-responsive{background:#0F1117}
      .cfm-dark .table>:not(caption)>*>*{border-color:#253048!important}
      .cfm-dark .table-hover>tbody>tr:hover>*{background-color:rgba(79,70,229,.09)!important;color:#CBD5E1!important}
      .cfm-dark .table-light{background:#1A2235!important;color:#7A90B4!important}
      .cfm-dark .table-active>*{background-color:rgba(79,70,229,.14)!important}
      .cfm-dark .form-control{background:#1A2235!important;color:#E2EBFF!important;border-color:#253048!important}
      .cfm-dark .form-control::placeholder{color:#4A5882}
      .cfm-dark .form-select{background:#1A2235!important;color:#E2EBFF!important;border-color:#253048!important}
      .cfm-dark textarea.form-control{background:#1A2235!important;color:#E2EBFF!important}
      .cfm-dark .input-group-text{background:#1E2A40!important;border-color:#253048!important;color:#7A90B4!important}
      .cfm-dark .btn-outline-secondary{color:#7A90B4;border-color:#253048}
      .cfm-dark .btn-outline-secondary:hover{background:#253048!important;color:#E2EBFF}
      .cfm-dark .btn-close{filter:invert(.7)}
      .cfm-dark .btn-link{color:#6B8BB4}
      .cfm-dark .btn-link.text-secondary{color:#7A90B4!important}
      .cfm-dark .btn-link.text-dark{color:#CBD5E1!important}
      .cfm-dark .btn-link.text-danger{color:#F87171!important}
      .cfm-dark .btn-link.text-warning{color:#FCD34D!important}
      .cfm-dark .btn-link.text-info{color:#67E8F9!important}
      .cfm-dark .alert-info{background:rgba(79,70,229,.14)!important;border-color:rgba(79,70,229,.3)!important;color:#A5B4FC!important}
      .cfm-dark .alert-warning{background:rgba(245,158,11,.12)!important;border-color:rgba(245,158,11,.25)!important;color:#FCD34D!important}
      .cfm-dark .alert-danger{background:rgba(239,68,68,.12)!important;border-color:rgba(239,68,68,.25)!important;color:#FCA5A5!important}
      .cfm-dark .badge.bg-secondary{background:#253048!important}
      .cfm-dark .text-muted{color:#5C7298!important}
      .cfm-dark .text-secondary{color:#7A90B4!important}
      .cfm-dark .text-dark{color:#E2EBFF!important}
      .cfm-dark .modal-content{background:#161C2C!important;border-color:#253048;color:#E2EBFF}
      .cfm-dark .modal-header,.cfm-dark .modal-footer{border-color:#253048}
      .cfm-dark .list-group-item{background:#161C2C!important;border-color:#253048!important;color:#CBD5E1!important}
      .cfm-dark .list-group-item-action:hover{background:rgba(79,70,229,.1)!important}
      .cfm-dark .progress{background:#253048}
      .cfm-dark .file-card-dark{background:#161C2C;border:1px solid #253048;border-radius:12px;transition:transform .18s ease,box-shadow .18s ease}
      .cfm-dark .file-card-dark:hover{transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,0,0,.5),0 0 0 1px #4F46E5}
      .cfm-light .file-card-light{background:#fff;border:1px solid #E2E8F0;border-radius:12px;transition:transform .18s ease,box-shadow .18s ease}
      .cfm-light .file-card-light:hover{transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,0,0,.1),0 0 0 1px #4F46E5}
      /* Scrollbars – dark */
      .cfm-dark ::-webkit-scrollbar{width:7px;height:7px}
      .cfm-dark ::-webkit-scrollbar-track{background:#0F1117}
      .cfm-dark ::-webkit-scrollbar-thumb{background:#2A3A54;border-radius:4px}
      .cfm-dark ::-webkit-scrollbar-thumb:hover{background:#374E70}
      .cfm-dark *{scrollbar-color:#2A3A54 #0F1117;scrollbar-width:thin}
      /* Scrollbars – light */
      .cfm-light ::-webkit-scrollbar{width:7px;height:7px}
      .cfm-light ::-webkit-scrollbar-track{background:#F1F5F9}
      .cfm-light ::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:4px}
      .cfm-light ::-webkit-scrollbar-thumb:hover{background:#94A3B8}
      .cfm-light *{scrollbar-color:#CBD5E1 #F1F5F9;scrollbar-width:thin}
    `;
  }, [darkMode]);

  // ✅ AJOUT : Listener resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setIsLoadingModels(true);
      setModelsError(null);
      try {
        const [keys, providersResp] = await Promise.all([
          apiKeyService.getApiKeys(),
          axios.get('/api/ai/providers').catch(() => ({ data: {} })),
        ]);
        const hasOpenAI = keys.some((k: any) => k.service === 'OpenAI' && k.isActive);
        const hasGoogleAI = keys.some((k: any) => k.service === 'GoogleAI' && k.isActive);
        const hasLTX = keys.some((k: any) => k.service === 'LTX' && k.isActive);
        const hasFalAI = !!(providersResp.data?.falai);

        const imageModels = [
          ...(hasOpenAI ? [{ id: 'dall-e-3', label: 'DALL·E 3', provider: 'OpenAI' as const }] : []),
          ...(hasGoogleAI ? [{ id: 'imagen-4.0-generate-001', label: 'Imagen 4', provider: 'GoogleAI' as const }] : []),
          ...(hasFalAI ? [
            { id: 'fal-ai/flux/dev', label: 'FLUX Dev (fal.ai)', provider: 'FalAI' as const },
            { id: 'fal-ai/flux/schnell', label: 'FLUX Schnell — rapide (fal.ai)', provider: 'FalAI' as const },
          ] : []),
        ];

        const videoModels = [
          ...(hasOpenAI ? [{ id: 'sora-2', label: 'Sora 2', provider: 'OpenAI' as const }] : []),
          ...(hasGoogleAI ? [{ id: 'veo-3.1-generate-preview', label: 'Veo 3.1 (preview)', provider: 'GoogleAI' as const }] : []),
          ...(hasLTX ? [
            { id: 'ltx-2-fast', label: 'LTX 2 Fast', provider: 'LTX' as const },
            { id: 'ltx-2-pro', label: 'LTX 2 Pro', provider: 'LTX' as const }
          ] : []),
          ...(hasFalAI ? [
            { id: 'bytedance/seedance-2.0/text-to-video',       label: 'Seedance 2.0 720p (fal.ai)',       provider: 'FalAI' as const },
            { id: 'bytedance/seedance-2.0/fast/text-to-video',  label: 'Seedance 2.0 Fast 720p (fal.ai)',  provider: 'FalAI' as const },
          ] : []),
        ];

        if (!cancelled) {
          setAvailableImageModels(imageModels);
          setAvailableVideoModels(videoModels);
          if (!selectedImageModel || !imageModels.some(m => m.id === selectedImageModel)) {
            setSelectedImageModel(imageModels[0]?.id || '');
          }
          if (!selectedVideoModel || !videoModels.some(m => m.id === selectedVideoModel)) {
            setSelectedVideoModel(videoModels[0]?.id || '');
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          setAvailableImageModels([]);
          setAvailableVideoModels([]);
          setSelectedImageModel('');
          setSelectedVideoModel('');
          setModelsError(error?.message || "Impossible de charger vos clés IA.");
        }
      } finally {
        if (!cancelled) setIsLoadingModels(false);
      }
    };

    loadModels();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const parentModal = containerRef.current.closest('.modal') as HTMLElement;
      const parentDialog = containerRef.current.closest('.modal-dialog') as HTMLElement;
      
      const HIGH_Z_INDEX = '10000'; 

      if (parentModal) parentModal.style.zIndex = HIGH_Z_INDEX;
      if (parentDialog) parentDialog.style.zIndex = String(Number(HIGH_Z_INDEX) + 10);
      
      const genericWrapper = containerRef.current.parentElement;
      if (genericWrapper && getComputedStyle(genericWrapper).position === 'fixed') {
        genericWrapper.style.zIndex = HIGH_Z_INDEX;
      }
    }
  }, []);

  useEffect(() => {
    const selectableFiles = files.filter(
      (file) => !file.isDir && (isText(file.name) || isImage(file.name) || isVideo(file.name))
    );
    setSelectedPromptFiles((prev) =>
      prev.filter((file) => selectableFiles.some((item) => item.url === file.url))
    );
    setSelectedItems((prev) => {
      const next: Record<string, { file: FileItem; path: string }> = {};
      files.forEach((file) => {
        const key = getItemKey(file);
        if (prev[key]) {
          next[key] = { file, path: getItemPath(file) };
        }
      });
      return next;
    });
  }, [files, currentPath]);

  const loadFiles = async (path: string): Promise<FileItem[]> => {
    try {
      setLoading(true);
      const list = await storageService.listFiles(path);
      const sorted = list.sort((a, b) => {
        if (a.isDir && !b.isDir) return -1;
        if (!a.isDir && b.isDir) return 1;
        const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        return dateB - dateA;
      });
      setFiles(sorted);
      return sorted;
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles(currentPath);
  }, [currentPath]);

  useEffect(() => {
    if (!bulkActionMode) return;
    let cancelled = false;
    const loadBulkFolders = async () => {
      try {
        setBulkActionLoading(true);
        const list = await storageService.listFiles(bulkActionPath);
        if (!cancelled) {
          setBulkActionFolders(
            list
              .filter((item) => item.isDir)
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        }
      } catch (err) {
        if (!cancelled) setBulkActionFolders([]);
      } finally {
        if (!cancelled) setBulkActionLoading(false);
      }
    };
    loadBulkFolders();
    return () => {
      cancelled = true;
    };
  }, [bulkActionMode, bulkActionPath]);

  useEffect(() => {
    setIsRenamingPreview(false);
    setPreviewRenameValue('');
    setIsEditingImage(false);
  }, [previewFile]);

  useEffect(() => {
    if (!previewFile) return;
    if (isVideo(previewFile.name)) {
      setContextMediaType('video');
    } else if (isImage(previewFile.name)) {
      setContextMediaType('image');
    }
  }, [previewFile]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const fileList = Array.from(e.target.files);
    const total = fileList.length;
    const errors: string[] = [];

    setUploading(true);
    setUploadProgress({ current: 0, total });

    for (let i = 0; i < fileList.length; i++) {
      setUploadProgress({ current: i + 1, total });
      try {
        await storageService.uploadFile(fileList[i], currentPath);
      } catch (err) {
        const msg = err instanceof Error ? err.message : `Erreur sur ${fileList[i].name}`;
        errors.push(msg);
      }
    }

    await loadFiles(currentPath);
    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (errors.length > 0) {
      alert(`${errors.length} fichier(s) en erreur :\n${errors.join('\n')}`);
    }
  };

  const handleSaveEditedImage = async (blob: Blob) => {
      if (!previewFile) return;
      
      try {
          setUploading(true);
          const file = new File([blob], previewFile.name, { type: blob.type });
          const newUrl = await storageService.uploadFile(file, currentPath, previewFile.name);
          
          await loadFiles(currentPath);
          setIsEditingImage(false);
          const cacheBusted = newUrl ? `${newUrl}${newUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : `${previewFile.url}?t=${Date.now()}`;
          setPreviewFile(prev => prev ? { ...prev, url: cacheBusted } : null);
          
      } catch (err) {
          const message = err instanceof Error ? err.message : "Erreur lors de la sauvegarde de l'image";
          alert(message);
          console.error(err);
      } finally {
          setUploading(false);
      }
  };

  const handleSaveEditedVideo = async (blob: Blob, filename: string) => {
      if (!previewFile) return;
      try {
          setUploading(true);
          const file = new File([blob], filename, { type: 'video/mp4' });
          const newUrl = await storageService.uploadFile(file, currentPath, filename);
          await loadFiles(currentPath);
          setIsEditingVideo(false);
          const cacheBusted = newUrl ? `${newUrl}${newUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : previewFile.url;
          setPreviewFile(prev => prev ? { ...prev, url: cacheBusted, name: filename } : null);
      } catch (err) {
          const message = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
          alert(message);
          console.error(err);
      } finally {
          setUploading(false);
      }
  };

  const handleSaveEditedAudio = async (blob: Blob, filename: string) => {
      try {
          setUploading(true);
          const mimeType = filename.endsWith('.json') ? 'application/json' : 'audio/wav';
          const file = new File([blob], filename, { type: mimeType });
          const newUrl = await storageService.uploadFile(file, currentPath, filename);
          await loadFiles(currentPath);
          setIsEditingAudio(false);
          if (previewFile && newUrl) {
              const cacheBusted = `${newUrl}${newUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
              setPreviewFile(prev => prev ? { ...prev, url: cacheBusted, name: filename } : null);
          }
      } catch (err) {
          alert(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
      } finally {
          setUploading(false);
      }
  };

  const handleCreateFolder = async () => {
    const name = prompt('Nom du dossier :');
    if (!name) return;
    
    try {
      const newPath = currentPath ? `${currentPath}/${name}` : name;
      await storageService.createFolder(newPath);
      await loadFiles(currentPath);
    } catch (err) {
      alert('Erreur création dossier');
    }
  };

  const getDeleteUrl = (url: string) => {
    const clean = url.split('?')[0].split('#')[0];
    const localMarkers = ['/api/storage/local/', '/api/uploads/'];
    const marker = localMarkers.find((m) => clean.includes(m));
    if (!marker) return clean;
    const start = clean.indexOf(marker);
    const prefix = clean.slice(start, start + marker.length);
    const rest = clean.slice(start + marker.length);
    const decoded = rest
      .split('/')
      .map((segment) => {
        try {
          return decodeURIComponent(segment);
        } catch {
          return segment;
        }
      })
      .join('/');
    return `${prefix}${decoded}`;
  };

  const isSameFileUrl = (a?: string, b?: string) => {
    if (!a || !b) return false;
    return getDeleteUrl(a) === getDeleteUrl(b);
  };

  const handleDelete = async (file: FileItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Supprimer ce fichier ?')) return;
    
    try {
      await storageService.deleteFile(getDeleteUrl(file.url));
      const list = await loadFiles(currentPath);
      setSelectedItems((prev) => {
        const next = { ...prev };
        delete next[getItemKey(file)];
        return next;
      });
      if (previewFile && isSameFileUrl(previewFile.url, file.url)) setPreviewFile(null);
      if (list.some((item) => getDeleteUrl(item.url) === getDeleteUrl(file.url))) {
        alert('La suppression n’a pas été confirmée côté serveur.');
      }
    } catch (err) {
      alert('Erreur suppression');
    }
  };

  const handleDeleteFolder = async (folderName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer le dossier "${folderName}" et tout son contenu ?`)) return;
    
    try {
      const pathToDelete = currentPath ? `${currentPath}/${folderName}` : folderName;
      await storageService.deleteFolder(pathToDelete);
      await loadFiles(currentPath);
      clearSelection();
      setPreviewFile(null);
    } catch (err) {
      alert('Erreur suppression dossier');
    }
  };

  const buildTargetPath = (dest: string, name: string) => (dest ? `${dest}/${name}` : name);

  const handleBulkDelete = async () => {
    if (selectedCount === 0) return;
    const hasFolder = selectedEntries.some((entry) => entry.file.isDir);
    const confirmMessage = hasFolder
      ? `Supprimer ${selectedCount} élément(s) (dossiers inclus) ?`
      : `Supprimer ${selectedCount} fichier(s) ?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      for (const entry of selectedEntries) {
        if (entry.file.isDir) {
          await storageService.deleteFolder(entry.path);
        } else {
          await storageService.deleteFile(getDeleteUrl(entry.file.url));
        }
      }
      clearSelection();
      await loadFiles(currentPath);
      if (previewFile && selectedEntries.some((entry) => isSameFileUrl(entry.file.url, previewFile.url))) {
        setPreviewFile(null);
      }
    } catch (err) {
      alert('Erreur suppression multiple');
    }
  };

  const openBulkActionModal = (mode: 'move' | 'copy') => {
    if (selectedCount === 0) return;
    setBulkActionMode(mode);
    setBulkActionPath('');
  };

  const closeBulkActionModal = () => {
    setBulkActionMode(null);
    setBulkActionPath('');
    setBulkActionFolders([]);
  };

  const handleBulkMove = () => openBulkActionModal('move');
  const handleBulkCopy = () => openBulkActionModal('copy');

  const confirmBulkAction = async () => {
    if (!bulkActionMode || selectedCount === 0) return;
    const destination = bulkActionPath;

    try {
      for (const entry of selectedEntries) {
        const targetPath = buildTargetPath(destination, entry.file.name);
        if (entry.file.isDir && (targetPath === entry.path || targetPath.startsWith(`${entry.path}/`))) {
          throw new Error('Destination invalide pour un dossier');
        }
        if (bulkActionMode === 'move') {
          if (targetPath === entry.path) continue;
          await storageService.moveItem(entry.path, targetPath, Boolean(entry.file.isDir));
        } else {
          await storageService.copyItem(entry.path, targetPath, Boolean(entry.file.isDir));
        }
      }
      await loadFiles(currentPath);
      if (bulkActionMode === 'move') {
        clearSelection();
        setPreviewFile(null);
      }
      closeBulkActionModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur opération multiple';
      alert(message);
    }
  };

  const handleRename = async (item: FileItem, newName: string) => {
    if (!newName || newName === item.name) return;

    try {
      const oldPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      const newPath = currentPath ? `${currentPath}/${newName}` : newName;
      await storageService.renameItem(oldPath, newPath);
      await loadFiles(currentPath);
      
      if (previewFile?.url === item.url) {
          setPreviewFile(null); 
      }
    } catch (err) {
      alert('Erreur renommage');
    }
  };

  const handleRenamePrompt = (item: FileItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const newName = prompt('Nouveau nom :', item.name);
    if (newName) handleRename(item, newName);
  };

  const submitPreviewRename = async () => {
    if (previewFile && previewRenameValue) {
        await handleRename(previewFile, previewRenameValue);
        setIsRenamingPreview(false);
    }
  };

  const handleNavigate = (folderName: string) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
    setPreviewFile(null);
    setSelectedItems({});
  };

  const handleGoUp = () => {
    if (!currentPath) return;
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
    setPreviewFile(null);
    setSelectedItems({});
  };

  const normalizePath = (value: string) =>
    value.replace(/^\/+/, '').replace(/\/+$/, '').replace(/\/{2,}/g, '/');

  const getItemPath = (file: FileItem, basePath = currentPath) => {
    const raw = basePath ? `${basePath}/${file.name}` : file.name;
    return normalizePath(raw);
  };

  const handleBulkNavigate = (folderName: string) => {
    const nextPath = bulkActionPath ? `${bulkActionPath}/${folderName}` : folderName;
    setBulkActionPath(normalizePath(nextPath));
  };

  const handleBulkGoUp = () => {
    if (!bulkActionPath) return;
    const parts = bulkActionPath.split('/');
    parts.pop();
    setBulkActionPath(parts.join('/'));
  };

  const getItemKey = (file: FileItem) => `${file.isDir ? 'dir' : 'file'}:${getItemPath(file)}`;

  const isItemSelected = (file: FileItem) => Boolean(selectedItems[getItemKey(file)]);

  const toggleItemSelection = (file: FileItem) => {
    const key = getItemKey(file);
    const path = getItemPath(file);
    setSelectedItems((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { file, path } };
    });
  };

  const clearSelection = () => setSelectedItems({});

  const selectedEntries = Object.values(selectedItems);
  const selectedCount = selectedEntries.length;
  const allSelected = files.length > 0 && files.every((file) => isItemSelected(file));

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      clearSelection();
      return;
    }
    const next: Record<string, { file: FileItem; path: string }> = {};
    files.forEach((file) => {
      const key = getItemKey(file);
      next[key] = { file, path: getItemPath(file) };
    });
    setSelectedItems(next);
  };

  const isImage = (filename: string) => /\.(jpg|jpeg|png|gif|webp|svg|avif|bmp|tiff?|heic|ico)$/i.test(filename);
  const isPdf = (filename: string) => /\.(pdf)$/i.test(filename);
  const isAudio = (filename: string) => /\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(filename);
  const isVideo = (filename: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(filename);
  const isText = (filename: string) => /\.(txt|md|csv|json|xml|yml|yaml|log|html|css|js|ts|tsx|jsx)$/i.test(filename);
  const isFbx = (filename: string) => /\.(fbx)$/i.test(filename);
  const isGlb = (filename: string) => /\.(glb|gltf)$/i.test(filename);

  const getVideoMimeType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const map: Record<string, string> = {
      mp4: 'video/mp4', webm: 'video/webm', ogg: 'video/ogg',
      mov: 'video/quicktime', avi: 'video/x-msvideo', mkv: 'video/x-matroska',
    };
    return map[ext] || 'video/mp4';
  };

  const getAudioMimeType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const map: Record<string, string> = {
      mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg',
      m4a: 'audio/mp4', flac: 'audio/flac', aac: 'audio/aac',
    };
    return map[ext] || 'audio/mpeg';
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'fa-file-pdf text-danger';
      case 'doc': case 'docx': return 'fa-file-word text-primary';
      case 'xls': case 'xlsx': return 'fa-file-excel text-success';
      case 'ppt': case 'pptx': return 'fa-file-powerpoint text-warning';
      case 'zip': case 'rar': return 'fa-file-archive text-secondary';
      case 'txt': return 'fa-file-alt text-muted';
      case 'mp4': case 'mov': case 'avi': return 'fa-file-video text-info';
      case 'mp3': case 'wav': return 'fa-file-audio text-info';
      case 'fbx': case 'glb': case 'gltf': return 'fa-cube text-primary';
      default: return 'fa-file text-secondary';
    }
  };

  const formatSize = (size?: number) => {
      if (size === undefined) return '-';
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr?: string) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString();
  };

  const selectablePromptFiles = files.filter(
    (file) => !file.isDir && (isText(file.name) || isImage(file.name) || isVideo(file.name))
  );
  const isPromptFileSelected = (file: FileItem) =>
    selectedPromptFiles.some((item) => item.url === file.url);

  const togglePromptFile = (file: FileItem) => {
    setSelectedPromptFiles((prev) => {
      if (prev.some((item) => item.url === file.url)) {
        return prev.filter((item) => item.url !== file.url);
      }
      return [...prev, file];
    });
  };

  const getNameFromUrl = (url: string, fallback: string) => {
    try {
      const pathPart = url.split('?')[0];
      const segments = pathPart.split('/');
      const last = segments[segments.length - 1];
      return decodeURIComponent(last || fallback);
    } catch {
      return fallback;
    }
  };

  const handlePromptLocalFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesToUpload = Array.from(e.target.files || []);
    if (filesToUpload.length === 0) return;
    try {
      setIsUploadingPromptFiles(true);
      const uploaded: FileItem[] = [];
      for (const file of filesToUpload) {
        const url = await storageService.uploadFile(file, currentPath);
        uploaded.push({
          name: getNameFromUrl(url, file.name),
          url,
          isDir: false
        });
      }
      await loadFiles(currentPath);
      setSelectedPromptFiles((prev) => {
        const map = new Map(prev.map((item) => [item.url, item]));
        uploaded.forEach((item) => map.set(item.url, item));
        return Array.from(map.values());
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de l'import des fichiers locaux.";
      alert(message);
    } finally {
      setIsUploadingPromptFiles(false);
      if (promptLocalInputRef.current) {
        promptLocalInputRef.current.value = '';
      }
    }
  };

  const resolvePromptFilePath = (file: FileItem) => {
    const rawUrl = file.url || '';
    try {
      const localMatch = rawUrl.match(/\/api\/storage\/local\/user-\d+\/(.+)$/);
      if (localMatch && localMatch[1]) return decodeURIComponent(localMatch[1]);
      const uploadsMatch = rawUrl.match(/\/api\/uploads\/user-\d+\/(.+)$/);
      if (uploadsMatch && uploadsMatch[1]) return decodeURIComponent(uploadsMatch[1]);
    } catch {
      // ignore decode errors
    }

    if (rawUrl.startsWith('http')) {
      try {
        const parsed = new URL(rawUrl);
        let path = parsed.pathname.replace(/^\/+/, '');
        if (parsed.hostname.startsWith('s3.') && path.includes('/')) {
          path = path.split('/').slice(1).join('/');
        }
        if (path) {
          try {
            return decodeURIComponent(path);
          } catch {
            return path;
          }
        }
      } catch {
        // ignore
      }
    }

    if (currentPath) return `${currentPath}/${file.name}`;
    return file.name;
  };

  const normalizePromptFileUrl = (url: string) => {
    if (!url) return url;
    return url.split('?')[0].split('#')[0];
  };

  const buildPromptFilesContext = async (filesToInject: FileItem[]) => {
    if (!filesToInject.length) return '';
    const MAX_TEXT_FILES = 5;
    const MAX_TEXT_CHARS = 4000;
    const textFiles = filesToInject.filter((file) => isText(file.name)).slice(0, MAX_TEXT_FILES);
    const mediaFiles = filesToInject.filter((file) => !isText(file.name)).slice(0, 15);

    const textSections: string[] = [];
    for (const file of textFiles) {
      const path = resolvePromptFilePath(file);
      try {
        const result = await storageService.getTextFile(path);
        const content = result.content || '';
        const truncatedBySize = content.length > MAX_TEXT_CHARS;
        const safeContent = truncatedBySize ? content.slice(0, MAX_TEXT_CHARS) : content;
        textSections.push(
          `[TEXT_FILE] ${file.name}\n${safeContent}${result.truncated || truncatedBySize ? '\n[TRUNCATED]' : ''}\n[/TEXT_FILE]`
        );
      } catch (error) {
        textSections.push(`[TEXT_FILE] ${file.name}\n[ERREUR LECTURE]\n[/TEXT_FILE]`);
      }
    }

    const mediaSection = mediaFiles.length
      ? `\n[MEDIA_FILES]\n${mediaFiles
          .map((file) => {
            const url = file.url ? normalizePromptFileUrl(file.url) : '';
            return `- ${file.name}${url ? ` (${url})` : ''}`;
          })
          .join('\n')}\n[/MEDIA_FILES]`
      : '';

    const blocks = [...textSections, mediaSection].filter(Boolean).join('\n');
    return blocks ? `\n\n[CONTEXT_FILES]\n${blocks}\n[/CONTEXT_FILES]` : '';
  };

  const buildImageSeriesContext = (filesToInject: FileItem[]) => {
    const imageFiles = filesToInject.filter((file) => isImage(file.name));
    if (!imageFiles.length) return '';
    const lines = imageFiles.map((file, idx) => {
      const url = file.url ? normalizePromptFileUrl(file.url) : '';
      return `${idx + 1}. ${file.name}${url ? ` (${url})` : ''}`;
    });
    return `\n\n[IMAGE_SERIES]\n${lines.join('\n')}\n[/IMAGE_SERIES]`;
  };

  // ✅ Helper pour savoir si on est en mode "Contextuel" (Image OU Vidéo)
  const isAIContextActive = previewFile && (isImage(previewFile.name) || isVideo(previewFile.name)) && showAIPanel;

  // ✅ Helper pour déterminer le type actuel (Contexte ou choix utilisateur)
  const currentAiType = isAIContextActive 
    ? contextMediaType
    : creationMediaType;

  const currentModelOptions = currentAiType === 'video' ? availableVideoModels : availableImageModels;
  const selectedModelId = currentAiType === 'video' ? selectedVideoModel : selectedImageModel;
  const selectedModel = currentModelOptions.find(m => m.id === selectedModelId);
  const setSelectedModelId = currentAiType === 'video' ? setSelectedVideoModel : setSelectedImageModel;
  const contextHasVideo = isAIContextActive && previewFile && isVideo(previewFile.name);
  const contextHasImage = isAIContextActive && previewFile && isImage(previewFile.name);
  const hasSelectedPromptVideo = selectedPromptFiles.some((file) => isVideo(file.name)) || !!contextHasVideo;
  const hasSelectedPromptImage = selectedPromptFiles.some((file) => isImage(file.name)) || !!contextHasImage;
  const isRetakeMode = currentAiType === 'video' && hasSelectedPromptVideo && !hasSelectedPromptImage;
  const isLtxSelected    = selectedModel?.provider === 'LTX';
  // Pour les tâches vidéo, toujours vérifier directement le modèle vidéo sélectionné
  // (selectedModel peut pointer sur un modèle image quand isAIContextActive est vrai)
  const selectedVideoModelObj = availableVideoModels.find(m => m.id === selectedVideoModel);
  const isComfySelected = false; // vast.ai supprimé — gardé pour compatibilité JSX
  const isFalAISelected = selectedVideoModelObj?.provider === 'FalAI';

  const renderComfyVideoSettings = () => (
    <div className="border rounded p-2 mb-3" style={{ background: tm.card, borderColor: tm.border }}>
      <div className="fw-bold small mb-2" style={{ color: tm.text }}>⚙️ Paramètres Seedance 2.0</div>

      {/* Durée + Résolution */}
      <div className="row g-2 mb-2">
        <div className="col-6">
          <label className="form-label small mb-1">Durée (s)</label>
          <input type="number" className="form-control form-control-sm" min={1} max={15} step={1}
            value={comfyDuration} onChange={e => setComfyDuration(Number(e.target.value))} disabled={isGeneratingAI} />
          <div style={{fontSize:'10px',color:'#6b7280'}}>max 15s</div>
        </div>
        <div className="col-6">
          <label className="form-label small mb-1">Résolution</label>
          <select className="form-select form-select-sm" value={comfyResolution}
            onChange={e => setComfyResolution(e.target.value as '480p' | '720p')} disabled={isGeneratingAI}>
            <option value="720p">720p — qualité</option>
            <option value="480p">480p — rapide</option>
          </select>
        </div>
      </div>

      {/* Format (aspect ratio) */}
      <div className="mb-2">
        <label className="form-label small mb-1">Format</label>
        <select className="form-select form-select-sm" value={comfyAspectRatio}
          onChange={e => setComfyAspectRatio(e.target.value)} disabled={isGeneratingAI}>
          <option value="16:9">16:9 — Paysage</option>
          <option value="9:16">9:16 — Portrait (mobile)</option>
          <option value="1:1">1:1 — Carré</option>
          <option value="4:3">4:3 — Standard</option>
          <option value="3:4">3:4 — Portrait standard</option>
          <option value="21:9">21:9 — Cinéma</option>
        </select>
      </div>

      {/* Seed */}
      <div className="mb-0">
        <label className="form-label small mb-1 d-flex align-items-center justify-content-between">
          <span>Seed</span>
          <div className="form-check form-switch mb-0 ms-2">
            <input className="form-check-input" type="checkbox" id="comfy-rand-seed"
              checked={comfyRandomizeSeed} onChange={e => setComfyRandomizeSeed(e.target.checked)} disabled={isGeneratingAI} />
            <label className="form-check-label small" htmlFor="comfy-rand-seed">Aléatoire</label>
          </div>
        </label>
        <div className="input-group input-group-sm">
          <input type="number" className="form-control form-control-sm" min={0} max={4294967295}
            value={comfySeed} onChange={e => setComfySeed(Number(e.target.value))}
            disabled={isGeneratingAI || comfyRandomizeSeed} />
          <button className="btn btn-outline-secondary btn-sm" type="button" title="Nouveau seed"
            onClick={() => setComfySeed(Math.floor(Math.random() * 2 ** 32))}
            disabled={isGeneratingAI || comfyRandomizeSeed}>↺</button>
        </div>
      </div>
    </div>
  );
  const LTX_SUPPORTED_MODELS = ['ltx-2-fast', 'ltx-2-pro'];
  const LTX_RESOLUTIONS = ['1920x1080', '2560x1440', '3840x2160'];
  const LTX_FPS_OPTIONS = [25, 50];
  const LTX_DURATION_MATRIX: Record<string, Record<string, Record<number, number[]>>> = {
    'ltx-2-fast': {
      '1920x1080': {
        25: [6, 8, 10, 12, 14, 16, 18, 20],
        50: [6, 8, 10]
      },
      '2560x1440': {
        25: [6, 8, 10],
        50: [6, 8, 10]
      },
      '3840x2160': {
        25: [6, 8, 10],
        50: [6, 8, 10]
      }
    },
    'ltx-2-pro': {
      '1920x1080': {
        25: [6, 8, 10],
        50: [6, 8, 10]
      },
      '2560x1440': {
        25: [6, 8, 10],
        50: [6, 8, 10]
      },
      '3840x2160': {
        25: [6, 8, 10],
        50: [6, 8, 10]
      }
    }
  };
  const LTX_PRICING: Record<string, Record<string, Record<string, number>>> = {
    'text-to-video': {
      'ltx-2-fast': { '1920x1080': 0.04, '2560x1440': 0.08, '3840x2160': 0.16 },
      'ltx-2-pro': { '1920x1080': 0.06, '2560x1440': 0.12, '3840x2160': 0.24 }
    },
    'image-to-video': {
      'ltx-2-fast': { '1920x1080': 0.04, '2560x1440': 0.08, '3840x2160': 0.16 },
      'ltx-2-pro': { '1920x1080': 0.06, '2560x1440': 0.12, '3840x2160': 0.24 }
    },
    'retake': {
      'ltx-2-pro': { '1920x1080': 0.10 }
    }
  };
  const getLtxAllowedResolutions = (modelId: string) => {
    if (!LTX_SUPPORTED_MODELS.includes(modelId)) return LTX_RESOLUTIONS;
    return LTX_RESOLUTIONS;
  };
  const getLtxAllowedFps = (modelId: string, resolution: string) => {
    if (!LTX_SUPPORTED_MODELS.includes(modelId)) return LTX_FPS_OPTIONS;
    return LTX_FPS_OPTIONS;
  };
  const getLtxAllowedDurations = (modelId: string, resolution: string, fps: number) => {
    const modelMatrix = LTX_DURATION_MATRIX[modelId];
    if (!modelMatrix) return [6, 8, 10];
    const resMatrix = modelMatrix[resolution] || modelMatrix['1920x1080'];
    return resMatrix[fps] || [6, 8, 10];
  };
  const clampToAllowedDuration = (modelId: string, resolution: string, fps: number, value: number) => {
    const allowed = getLtxAllowedDurations(modelId, resolution, fps);
    if (allowed.includes(value)) return value;
    return allowed[0];
  };
  const resolveAutoResolution = (width?: number, height?: number) => {
    if (!width || !height) return '1920x1080';
    const maxSide = Math.max(width, height);
    if (maxSide >= 3500) return '3840x2160';
    if (maxSide >= 2200) return '2560x1440';
    return '1920x1080';
  };
  const buildContextFiles = () => {
    const map = new Map<string, { name: string; url: string }>();
    const addFile = (file?: FileItem | null) => {
      if (!file?.url) return;
      const url = normalizePromptFileUrl(file.url);
      if (!url) return;
      map.set(url, { name: file.name, url });
    };

    // En mode édition (fichier en aperçu), on garde uniquement ce média comme source.
    // Les autres fichiers restent injectés dans le prompt (buildPromptFilesContext).
    if (isAIContextActive && previewFile) {
      addFile(previewFile);
      if (map.size > 0) return Array.from(map.values());
    }

    selectedPromptFiles.forEach(addFile);
    return Array.from(map.values());
  };

  useEffect(() => {
    if (selectedModel?.provider !== 'LTX' || currentAiType !== 'video') return;
    if (isRetakeMode && selectedModel.id !== 'ltx-2-pro') {
      setSelectedVideoModel('ltx-2-pro');
    }
  }, [selectedModel, currentAiType, isRetakeMode, setSelectedVideoModel]);

  useEffect(() => {
    if (selectedModel?.provider !== 'LTX' || currentAiType !== 'video') return;
    if (isRetakeMode) {
      setLtxResolution('1920x1080');
      setLtxFps(25);
      if (ltxRetakeStartTime < 0) setLtxRetakeStartTime(0);
      if (ltxRetakeDuration < 2) setLtxRetakeDuration(2);
      if (ltxRetakeDuration > 20) setLtxRetakeDuration(20);
      return;
    }
    const allowedRes = getLtxAllowedResolutions(selectedModel.id);
    if (!allowedRes.includes(ltxResolution)) {
      setLtxResolution(allowedRes[0]);
    }
    const allowedFps = getLtxAllowedFps(selectedModel.id, ltxResolution);
    if (!allowedFps.includes(ltxFps)) {
      setLtxFps(allowedFps[0]);
    }
    const clamped = clampToAllowedDuration(selectedModel.id, ltxResolution, ltxFps, ltxDuration);
    if (clamped !== ltxDuration) {
      setLtxDuration(clamped);
    }
  }, [selectedModel, currentAiType, isRetakeMode, ltxResolution, ltxFps, ltxDuration]);

  useEffect(() => {
    if (selectedModel?.provider !== 'LTX' || currentAiType !== 'video' || ltxPreset !== 'auto') return;
    let cancelled = false;
    const resolveSource = (): { type: 'image' | 'video'; url: string } | null => {
      if (isAIContextActive && previewFile && (isImage(previewFile.name) || isVideo(previewFile.name))) {
        return { type: isVideo(previewFile.name) ? 'video' : 'image', url: previewFile.url };
      }
      const firstImage = selectedPromptFiles.find((file) => isImage(file.name));
      if (firstImage) return { type: 'image', url: firstImage.url };
      const firstVideo = selectedPromptFiles.find((file) => isVideo(file.name));
      if (firstVideo) return { type: 'video', url: firstVideo.url };
      return null;
    };

    const source = resolveSource();
    if (!source) {
      setLtxSourceMeta(null);
      return;
    }

    const loadImageMeta = (url: string) =>
      new Promise<{ width: number; height: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = url;
      });

    const loadVideoMeta = (url: string) =>
      new Promise<{ width: number; height: number; duration: number }>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration });
        video.onerror = () => reject();
        video.src = url;
      });

    const applyAuto = async () => {
      try {
        if (source.type === 'image') {
          const meta = await loadImageMeta(source.url);
          if (cancelled) return;
          setLtxSourceMeta({ type: 'image', width: meta.width, height: meta.height });
          setLtxFps(25);
          const autoRes = resolveAutoResolution(meta.width, meta.height);
          setLtxResolution(autoRes);
          const desired = selectedModel.id === 'ltx-2-fast' ? 10 : 8;
          const clamped = clampToAllowedDuration(selectedModel.id, autoRes, 25, desired);
          setLtxDuration(clamped);
        } else {
          const meta = await loadVideoMeta(source.url);
          if (cancelled) return;
          setLtxSourceMeta({ type: 'video', width: meta.width, height: meta.height, duration: meta.duration });
          if (isRetakeMode) {
            const maxDuration = Number.isFinite(meta.duration) ? Math.floor(meta.duration) : 6;
            const adjusted = Math.max(2, Math.min(20, maxDuration));
            setLtxRetakeStartTime(0);
            setLtxRetakeDuration(adjusted);
          } else {
            setLtxFps(25);
            const autoRes = resolveAutoResolution(meta.width, meta.height);
            setLtxResolution(autoRes);
            const desired = selectedModel.id === 'ltx-2-fast' ? 10 : 8;
            const clamped = clampToAllowedDuration(selectedModel.id, autoRes, 25, desired);
            setLtxDuration(clamped);
          }
        }
      } catch {
        setLtxSourceMeta({ type: source.type });
      }
    };

    applyAuto();

    return () => {
      cancelled = true;
    };
  }, [
    selectedModel,
    currentAiType,
    ltxPreset,
    isAIContextActive,
    previewFile,
    selectedPromptFiles,
    isRetakeMode
  ]);

  useEffect(() => {
    if (!isLtxSelected) {
      setLtxCreditsBlocked(false);
      setLtxCreditsMessage(null);
    }
  }, [isLtxSelected]);

  // ✅ HELPER : Injection des dimensions dans le prompt
  const getAugmentedPrompt = (originalPrompt: string) => {
    if (!generationContext || !generationContext.width || !generationContext.height) return originalPrompt;
    
    const { width, height, target } = generationContext;
    const ratio = width / height;
    
    let technicalInstructions = `\n[constraint : aspect-ratio ${ratio.toFixed(2)}:1] Generate content for a ${target}. Target resolution approx: ${width}x${height}px.`;
    
    if (ratio > 2) {
        technicalInstructions += " Composition must be WIDE / PANORAMIC.";
    } else if (ratio < 0.5) {
        technicalInstructions += " Composition must be TALL / VERTICAL.";
    }
    
    return `${originalPrompt} ${technicalInstructions}`;
  };

  // ✅ FONCTION DE GÉNÉRATION CORRIGÉE & INTELLIGENTE (Support SORA)
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return;

    // Pour les tâches vidéo du studio, forcer type='video' et utiliser le modèle vidéo sélectionné
    // (indépendamment de isAIContextActive qui pourrait renvoyer 'image')
    const videoStudioTasks = ['video-from-prompt', 'video-from-image', 'video-img-comfy', 'continue-video'];
    const isVideoStudioTask = videoStudioTasks.includes(studioTask || '');
    const effectiveType: 'image' | 'video' = isVideoStudioTask ? 'video' : currentAiType;
    const effectiveModel = isVideoStudioTask
      ? (availableVideoModels.find(m => m.id === selectedVideoModel) ?? selectedModel)
      : selectedModel;

    if (!effectiveModel) {
      alert("Aucun modèle IA disponible. Ajoutez une clé OpenAI ou Google AI Studio dans vos paramètres.");
      return;
    }
    
    try {
      setIsGeneratingAI(true);
      setAiProgress(null); // Reset progress
      
      // ✅ Application du contexte dimensionnel au prompt
      let finalPrompt = getAugmentedPrompt(aiPrompt);
      const promptFiles = (() => {
        const map = new Map<string, FileItem>();
        if (isAIContextActive && previewFile) {
          const key = previewFile.url || previewFile.name;
          map.set(key, previewFile);
        }
        selectedPromptFiles.forEach((file) => {
          const key = file.url || file.name;
          if (!map.has(key)) map.set(key, file);
        });
        return Array.from(map.values());
      })();
      const filesContext = await buildPromptFilesContext(promptFiles);
      if (filesContext) {
        finalPrompt = `${finalPrompt}${filesContext}`;
      }
      if (currentAiType === 'video' && !finalPrompt.includes('[IMAGE_SERIES]')) {
        const imageSeries = buildImageSeriesContext(promptFiles);
        if (imageSeries) {
          finalPrompt = `${finalPrompt}${imageSeries}`;
        }
      }
      const contextFilesPayload = buildContextFiles();
      
      console.log(`Prompt généré pour ${generationContext?.target || 'défaut'}:`, finalPrompt);
      console.log('📎 Fichiers joints envoyés:', contextFilesPayload.map((f) => f.name));

      // Simulation de prompt contextuel
      if (isAIContextActive && previewFile) {
         // finalPrompt = `[Based on ${previewFile.name}] ${aiPrompt}`;
      }

      // 1. Lancement de la génération
      // Si c'est une vidéo (Sora), le backend retournera peut-être un { processingId, status: 'queued' }
      const effectiveSeed = comfyRandomizeSeed ? Math.floor(Math.random() * 2 ** 32) : comfySeed;
      const response: any = await generateAIMedia(
        finalPrompt,
        effectiveType,
        currentPath,
        contextFilesPayload,
        effectiveModel.id,
        effectiveModel.provider,
        effectiveModel.provider === 'LTX'
          ? {
              duration: ltxDuration,
              fps: ltxFps,
              resolution: ltxResolution,
              generateAudio: ltxGenerateAudio,
              retakeStartTime: ltxRetakeMode === 'extend'
                ? Math.max(0, (ltxSourceMeta?.duration ?? ltxRetakeDuration) - ltxRetakeDuration)
                : ltxRetakeStartTime,
              retakeDuration: ltxRetakeDuration,
              retakeMode: ltxRetakeMode === 'extend' ? 'replace_audio_and_video' : ltxRetakeMode,
              useUpload: ltxUseUpload
            }
          : undefined,
        (effectiveModel.provider === 'FalAI')
          ? {
              duration:    comfyDuration,
              resolution:  comfyResolution,
              aspectRatio: comfyAspectRatio,
              seed:        effectiveSeed,
              useI2V:      studioTask === 'video-img-comfy' || studioTask === 'video-from-image',
            }
          : undefined
      );
      
      console.log("Réponse IA init:", response);

      // ✅ GESTION DU FLUX ASYNCHRONE (SORA / FalAI)
      if (response && (response.processingId || response.jobId)) {
          const jobId = response.processingId || response.jobId;
          let isFinished = false;
          let attempts = 0;

          // Boucle de polling (Vérification toutes les 5 secondes)
          while (!isFinished) {
              attempts++;
              // Attendre 5s avant de vérifier
              await new Promise(resolve => setTimeout(resolve, 5000));

              // Appel au service (il faut s'assurer que cette fonction est exportée dans aiService.ts)
              // Nous importons checkAIMediaStatus dynamiquement ou supposons son existence pour l'instant
              // @ts-ignore
              const { checkAIMediaStatus } = await import('../services/aiService');
              let statusData: any = { status: 'in_progress' };
              try {
                statusData = await checkAIMediaStatus(jobId);
              } catch (pollErr: any) {
                // Erreur réseau / timeout transitoire → on continue le polling
                console.warn('Polling transient error, retrying...', pollErr?.message);
              }

              console.log("Media Status:", statusData);

              if (statusData.progress !== undefined) {
                  setAiProgress(statusData.progress);
              }

              if (statusData.status === 'completed') {
                  isFinished = true;
                  setAiProgress(100);
                  alert(`Vidéo générée : ${statusData.filename || 'fichier créé'}`);
              } else if (statusData.status === 'failed' || statusData.status === 'cancelled') {
                  throw new Error(`La génération vidéo a échoué: ${statusData.error || 'Erreur inconnue'}`);
              } else {
                  // status est 'queued' ou 'in_progress'
                  // On continue la boucle
              }

              // Timeout de sécurité côté front (10 minutes pour vidéos longues)
              if (attempts > 120) throw new Error("Timeout: La génération prend trop de temps.");
          }
      }

      // 2. Rafraîchir la liste après complétion ou génération synchrone (DALL-E)
      await loadFiles(currentPath);
      
      setAiPrompt('');
      setSelectedPromptFiles([]);
      setPromptFilePickerOpen(false);
      setAiProgress(null);
      
      // On ferme le panneau si ce n'était pas contextuel
      if (!isAIContextActive) {
        setShowAIPanel(false); 
      }

      // Notification finale si pas déjà faite
      if (!response.processingId && !response.jobId) {
          const typeLabel = effectiveType === 'video' ? 'Vidéo' : 'Image';
          alert(`${typeLabel} générée avec succès !`);
      }

    } catch (err: any) {
      console.error(err);
      if (err?.response?.status === 402 && effectiveModel?.provider === 'LTX') {
        setLtxCreditsBlocked(true);
        setLtxCreditsMessage("Crédits LTX insuffisants. Rechargez votre compte LTX puis rechargez la page.");
        alert("Crédits LTX insuffisants. Rechargez votre compte LTX puis rechargez la page.");
        return;
      }
      alert(`Erreur IA: ${err.response?.data?.error || err.message || 'Erreur inconnue'}`);
    } finally {
      setIsGeneratingAI(false);
      setAiProgress(null);
    }
  };

  // ── AI Studio : sélection d'une tâche ─────────────────────────────────
  const handleTaskSelect = (taskId: string) => {
    setOcrResult(null);
    setOcrSourceFile(null);
    setTtsText('');
    setTtsVideoFile(null);
    setSubVideoFile(null);
    setSubtitleSrt(null);
    setAiPrompt('');
    setSelectedPromptFiles([]);
    if (taskId === 'create-image') {
      setIsCreatingImage(true);
      setShowAIPanel(false);
    } else if (taskId === 'create-video') {
      setIsCreatingVideo(true);
      setShowAIPanel(false);
    } else if (taskId === 'image-from-prompt') {
      setCreationMediaType('image');
      setStudioTask(taskId);
    } else if (taskId === 'video-from-prompt') {
      setCreationMediaType('video');
      setStudioTask(taskId);
    } else if (taskId === 'video-from-image') {
      setCreationMediaType('video');
      if (previewFile && isImage(previewFile.name)) setSelectedPromptFiles([previewFile]);
      setStudioTask(taskId);
    } else if (taskId === 'video-img-comfy') {
      setCreationMediaType('video');
      if (previewFile && isImage(previewFile.name)) setSelectedPromptFiles([previewFile]);
      // Auto-sélectionner Wan2.1 fal.ai pour I2V
      const wanModel = availableVideoModels.find(m => m.provider === 'FalAI' && m.id.includes('wan'));
      if (wanModel) setSelectedVideoModel(wanModel.id);
      setStudioTask(taskId);
    } else if (taskId === 'continue-video') {
      setCreationMediaType('video');
      if (previewFile && isVideo(previewFile.name)) setSelectedPromptFiles([previewFile]);
      setLtxRetakeStartTime(0);
      setStudioTask(taskId);
    } else if (taskId === 'tts-video') {
      if (previewFile && isVideo(previewFile.name)) setTtsVideoFile(previewFile);
      setStudioTask(taskId);
    } else if (taskId === 'subtitles') {
      if (previewFile && isVideo(previewFile.name)) setSubVideoFile(previewFile);
      setStudioTask(taskId);
    } else if (taskId === 'edit-image') {
      if (previewFile && isImage(previewFile.name)) {
        setIsEditingImage(true);
        setShowAIPanel(false);
      } else {
        setStudioTask(taskId);
      }
    } else if (taskId === 'edit-video') {
      if (previewFile && isVideo(previewFile.name)) {
        setIsEditingVideo(true);
        setShowAIPanel(false);
      } else {
        setStudioTask(taskId);
      }
    } else if (taskId === 'edit-audio') {
      if (previewFile && isAudio(previewFile.name)) {
        setIsEditingAudio(true);
        setShowAIPanel(false);
      } else {
        setStudioTask(taskId);
      }
    } else {
      setStudioTask(taskId);
    }
  };

  // ── AI Studio : OCR (olmOCR port 8081 via backend) ────────────────────
  const handleOcr = async () => {
    const sourceFile = ocrSourceFile
      || (isAIContextActive && previewFile && isImage(previewFile.name) ? previewFile : null);
    if (!sourceFile) return;
    setIsRunningOcr(true);
    setOcrResult(null);
    try {
      const { data } = await axios.post('/api/ai/ocr', {
        imageUrl: getPublicSelectUrl(sourceFile.url)
      });
      setOcrResult(data.text ?? '');
    } catch (err: any) {
      alert(`Erreur OCR : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsRunningOcr(false);
    }
  };

  const handleDownloadOcrText = () => {
    if (!ocrResult) return;
    const blob = new Blob([ocrResult], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'texte_extrait.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── TTS-Video handler ──────────────────────────────────────────────────────
  const handleTtsVideo = async () => {
    const sourceFile = ttsVideoFile || (previewFile && isVideo(previewFile.name) ? previewFile : null);
    if (!sourceFile || !ttsText.trim()) return;
    setIsRunningTts(true);
    try {
      const { data } = await axios.post('/api/ai/tts-video', {
        videoUrl: getPublicSelectUrl(sourceFile.url),
        text: ttsText,
        voice: ttsVoice,
        model: ttsModelQ,
        currentPath: currentPath || '/',
      });
      alert(`✅ Vidéo générée : ${data.filename}`);
      await loadFiles(currentPath);
    } catch (err: any) {
      alert(`Erreur TTS-Video : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsRunningTts(false);
    }
  };

  // ── Subtitles handler ─────────────────────────────────────────────────────
  const handleSubtitles = async () => {
    const sourceFile = subVideoFile || (previewFile && isVideo(previewFile.name) ? previewFile : null);
    if (!sourceFile) return;
    setIsRunningSubtitles(true);
    setSubtitleSrt(null);
    try {
      const { data } = await axios.post('/api/ai/subtitles', {
        videoUrl: getPublicSelectUrl(sourceFile.url),
        language: subLanguage,
        hardcode: subHardcode,
        fontName: subFont,
        fontSize: subFontSize,
        textColor: subColor,
        outlineColor: subOutline,
        bold: subBold,
        currentPath: currentPath || '/',
      });
      setSubtitleSrt(data.srt || null);
      alert(`✅ ${subHardcode ? 'Vidéo sous-titrée' : 'Fichier SRT'} : ${data.filename}`);
      await loadFiles(currentPath);
    } catch (err: any) {
      alert(`Erreur sous-titrage : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsRunningSubtitles(false);
    }
  };

  // ── Theme tokens ────────────────────────────────────────────────────────────
  const tm = {
    bg:        darkMode ? '#0F1117'               : '#ffffff',
    surface:   darkMode ? '#1A2235'               : '#F8FAFC',
    surface2:  darkMode ? '#1E2A3A'               : '#EFF3FA',
    card:      darkMode ? '#161C2C'               : '#ffffff',
    border:    darkMode ? '#253048'               : '#E2E8F0',
    text:      darkMode ? '#E2EBFF'               : '#1A2030',
    muted:     darkMode ? '#5C7298'               : '#6B7280',
    sub:       darkMode ? '#A8BDD8'               : '#374151',
    header:    darkMode ? '#111827'               : '#ffffff',
    input:     darkMode ? '#1A2235'               : '#ffffff',
    accent:    '#4F46E5',
    shadow:    darkMode ? '0 1px 8px rgba(0,0,0,.5)' : '0 1px 4px rgba(0,0,0,.08)',
  };

  const content = (
    <div ref={containerRef} className={`cloud-file-manager card h-100 shadow-lg ${darkMode ? 'cfm-dark' : 'cfm-light'}`} style={{ ...(isOverlay ? { maxHeight: '85vh', maxWidth: '1200px', width: '90%' } : {}), background: tm.bg, color: tm.text }}>
      {isEditingImage && previewFile && createPortal(
          <ImageEditor
            file={previewFile}
            onSave={handleSaveEditedImage}
            onCancel={() => setIsEditingImage(false)}
          />,
          document.body
      )}

      {isEditingVideo && previewFile && createPortal(
          <VideoEditor
            file={previewFile}
            availableFiles={files}
            currentPath={currentPath}
            onSave={handleSaveEditedVideo}
            onCancel={() => setIsEditingVideo(false)}
          />,
          document.body
      )}

      {isEditingAudio && createPortal(
          <AudioEditor
            file={previewFile || { name: '', url: '' }}
            onSave={handleSaveEditedAudio}
            onCancel={() => setIsEditingAudio(false)}
          />,
          document.body
      )}

      {isCreatingImage && createPortal(
          <ImageEditor
            file={{ name: '', url: '' }}
            onSave={async (blob: Blob) => { setIsCreatingImage(false); await handleSaveEditedImage(blob); }}
            onCancel={() => setIsCreatingImage(false)}
          />,
          document.body
      )}

      {isCreatingVideo && createPortal(
          <VideoEditor
            file={{ name: '', url: '' }}
            availableFiles={files}
            currentPath={currentPath}
            onSave={async (blob: Blob, filename: string) => { setIsCreatingVideo(false); await handleSaveEditedVideo(blob, filename); }}
            onCancel={() => setIsCreatingVideo(false)}
          />,
          document.body
      )}

      {bulkActionMode && createPortal(
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 10050 }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {bulkActionMode === 'move' ? 'Déplacer vers…' : 'Copier vers…'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeBulkActionModal} aria-label="Fermer"></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex align-items-center bg-light p-2 rounded mb-3">
                    <button
                      className="btn btn-link btn-sm p-0 me-2 text-decoration-none"
                      onClick={() => setBulkActionPath('')}
                      disabled={!bulkActionPath}
                    >
                      <i className="fas fa-home"></i>
                    </button>
                    {bulkActionPath && (
                      <>
                        <span className="text-muted mx-1">/</span>
                        <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={handleBulkGoUp}>
                          <i className="fas fa-level-up-alt"></i> ..
                        </button>
                        <span className="text-muted mx-1">/</span>
                        <span className="text-truncate">{bulkActionPath}</span>
                      </>
                    )}
                  </div>

                  {bulkActionLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary"></div>
                    </div>
                  ) : bulkActionFolders.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="fas fa-folder-open fa-2x mb-2"></i>
                      <div>Aucun dossier</div>
                    </div>
                  ) : (
                    <div className="list-group">
                      {bulkActionFolders.map((folder, index) => (
                        <button
                          key={`${folder.name}-${index}`}
                          type="button"
                          className="list-group-item list-group-item-action d-flex align-items-center"
                          onClick={() => handleBulkNavigate(folder.name)}
                        >
                          <i className="fas fa-folder text-warning me-2"></i>
                          <span className="text-truncate">{folder.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto text-muted small">
                    Destination : /{bulkActionPath || ''}
                  </div>
                  <button className="btn btn-outline-secondary" onClick={closeBulkActionModal}>
                    Annuler
                  </button>
                  <button className="btn btn-primary" onClick={confirmBulkAction}>
                    {bulkActionMode === 'move' ? 'Déplacer ici' : 'Copier ici'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 10040 }} onClick={closeBulkActionModal}></div>
        </>,
        document.body
      )}

      {/* ── HEADER ───────────────────────────────────────────────────────────── */}
      <div className="card-header" style={{ background: tm.header, borderBottom: `1px solid ${tm.border}`, padding: '10px 14px 0' }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 text-truncate d-flex align-items-center gap-2" style={{ fontSize: 15, fontWeight: 700, color: tm.text }}>
            <span style={{ color: tm.accent }}>☁</span>
            <AutoTranslateText sourceLang="fr">Mes Fichiers</AutoTranslateText>
          </h5>
          <div className="d-flex gap-1 align-items-center">

            {/* Bouton AI Studio */}
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              title={isAIContextActive ? "Modifier avec l'IA" : "AI Studio"}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 11px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                border: `1px solid ${showAIPanel ? tm.accent : tm.border}`,
                background: showAIPanel ? tm.accent : 'transparent',
                color: showAIPanel ? '#fff' : (isAIContextActive ? '#F59E0B' : tm.sub),
                cursor: 'pointer', transition: 'all .15s ease',
              }}
            >
              <i className={`fas ${isAIContextActive ? 'fa-wand-magic-sparkles' : 'fa-magic'}`}></i>
              <span className="d-none d-md-inline">{isAIContextActive ? 'AI Edit' : 'AI Studio'}</span>
            </button>

            {/* Vue grille / liste */}
            <div className="d-none d-sm-flex" style={{ gap: 2 }}>
              {(['grid', 'list'] as const).map(v => (
                <button key={v} onClick={() => setViewMode(v)} disabled={!!previewFile}
                  title={v === 'grid' ? 'Vue grille' : 'Vue liste'}
                  style={{
                    width: 30, height: 30, borderRadius: 7, border: `1px solid ${tm.border}`,
                    background: viewMode === v ? tm.accent : 'transparent',
                    color: viewMode === v ? '#fff' : tm.muted,
                    cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <i className={`fas ${v === 'grid' ? 'fa-th-large' : 'fa-list'}`}></i>
                </button>
              ))}
            </div>

            {/* Tout sélectionner */}
            {viewMode === 'grid' && !previewFile && (
              <button onClick={() => toggleSelectAll(!allSelected)} title={allSelected ? 'Désélectionner' : 'Tout sélectionner'}
                style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${tm.border}`, background: 'transparent', color: tm.muted, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={`fas ${allSelected ? 'fa-times-circle' : 'fa-check-square'}`}></i>
              </button>
            )}

            {/* Nouveau dossier */}
            <button onClick={handleCreateFolder} title="Nouveau dossier"
              style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${tm.border}`, background: 'transparent', color: tm.muted, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-folder-plus"></i>
            </button>

            {/* Upload */}
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleUpload} multiple />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 8,
                fontSize: 12, fontWeight: 600, border: 'none', cursor: uploading ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(90deg,#4F46E5,#06B6D4)', color: '#fff', opacity: uploading ? 0.7 : 1,
              }}>
              {uploading && uploadProgress
                ? <><span className="spinner-border spinner-border-sm"></span><span className="d-none d-sm-inline ms-1">{uploadProgress.current}/{uploadProgress.total}</span></>
                : <><i className="fas fa-upload"></i><span className="d-none d-sm-inline ms-1"><AutoTranslateText sourceLang="fr">Uploader</AutoTranslateText></span></>
              }
            </button>

            {onClose && (
              <button onClick={onClose} aria-label="Fermer"
                style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${tm.border}`, background: 'transparent', color: tm.muted, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 4 }}>
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: tm.surface, borderRadius: 8, padding: '5px 10px', marginBottom: 8, border: `1px solid ${tm.border}`, fontSize: 13 }}>
          <button onClick={() => { setCurrentPath(''); setPreviewFile(null); }} disabled={!currentPath}
            style={{ background: 'none', border: 'none', color: currentPath ? tm.accent : tm.muted, cursor: currentPath ? 'pointer' : 'default', padding: '0 2px' }}>
            <i className="fas fa-home"></i>
          </button>
          {currentPath && (
            <>
              <span style={{ color: tm.muted }}>/</span>
              <button onClick={handleGoUp}
                style={{ background: 'none', border: 'none', color: tm.accent, cursor: 'pointer', padding: '0 2px', fontSize: 12 }}>
                <i className="fas fa-level-up-alt"></i> ..
              </button>
              <span style={{ color: tm.muted }}>/</span>
              <span style={{ color: tm.text, fontWeight: 500 }} className="text-truncate">{currentPath}</span>
            </>
          )}
        </div>

        {/* Barre d'actions groupées */}
        {selectedCount > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 8, alignItems: 'center' }}>
            <span style={{ background: tm.accent, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20 }}>{selectedCount} sélectionné(s)</span>
            {[
              { label: 'Supprimer', icon: 'fa-trash', color: '#EF4444', fn: handleBulkDelete },
              { label: 'Déplacer',  icon: 'fa-arrows-alt', color: tm.sub, fn: handleBulkMove },
              { label: 'Copier',   icon: 'fa-copy', color: tm.sub, fn: handleBulkCopy },
              { label: 'Annuler',  icon: 'fa-times', color: tm.muted, fn: clearSelection },
            ].map(a => (
              <button key={a.label} onClick={a.fn}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 7, border: `1px solid ${tm.border}`, background: 'transparent', color: a.color, fontSize: 12, cursor: 'pointer' }}>
                <i className={`fas ${a.icon}`}></i>{a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="card-body p-0 d-flex position-relative" style={{ overflow: 'hidden' }}>
        
        {/* ✅ PANNEAU LATÉRAL AI STUDIO */}
        {showAIPanel && (
            <div className="p-3 d-flex flex-column" style={{
                width: isMobile ? '100%' : '300px',
                minWidth: isMobile ? '100%' : '280px',
                position: isMobile ? 'absolute' : 'relative',
                zIndex: 30,
                height: '100%',
                top: 0, left: 0,
                background: tm.surface,
                borderRight: `1px solid ${tm.border}`,
                boxShadow: tm.shadow,
                overflowY: 'auto',
                color: tm.text,
            }}>
                {/* ── HEADER ─────────────────────────────────────── */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold d-flex align-items-center gap-2" style={{ color: tm.text }}>
                        {studioTask && (
                            <button
                                style={{ background: 'none', border: 'none', color: tm.sub, cursor: 'pointer', padding: '0 4px' }}
                                onClick={() => { setStudioTask(null); setOcrResult(null); }}
                                title="Retour"
                            >
                                <i className="fas fa-arrow-left"></i>
                            </button>
                        )}
                        <i className="fas fa-magic" style={{ color: tm.accent }}></i>
                        {studioTask
                            ? (STUDIO_TASKS.find(t => t.id === studioTask)?.label.replace('\n', ' ') ?? 'Studio')
                            : 'AI Studio'}
                    </h6>
                    <button style={{ background: 'none', border: 'none', color: tm.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1 }} onClick={() => { setShowAIPanel(false); setStudioTask(null); }}>✕</button>
                </div>

                {/* ── TASK GRID ─────────────────────────────────────────────────── */}
                {!studioTask ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', overflowY: 'auto' }}>
                        {STUDIO_TASKS.map((task) => (
                            <button
                                key={task.id}
                                onClick={() => !task.soon && handleTaskSelect(task.id)}
                                style={{
                                    position: 'relative',
                                    border: `2px solid ${task.soon ? tm.border : task.color}`,
                                    borderRadius: '10px',
                                    backgroundColor: tm.card,
                                    padding: '10px 4px 8px',
                                    cursor: task.soon ? 'default' : 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '6px',
                                    opacity: task.soon ? 0.55 : 1,
                                    transition: 'transform 0.15s ease, background-color 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                    if (!task.soon) {
                                        e.currentTarget.style.backgroundColor = task.color + '22';
                                        e.currentTarget.style.transform = 'scale(1.04)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!task.soon) {
                                        e.currentTarget.style.backgroundColor = tm.card;
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }
                                }}
                            >
                                {task.soon && (
                                    <span style={{
                                        position: 'absolute', top: '3px', right: '3px',
                                        fontSize: '8px', background: tm.muted, color: '#fff',
                                        borderRadius: '4px', padding: '1px 4px', lineHeight: 1.4
                                    }}>Bientôt</span>
                                )}
                                <span style={{ fontSize: '22px', lineHeight: 1 }}>{task.icon}</span>
                                <span style={{
                                    fontSize: '10px', fontWeight: 600, textAlign: 'center',
                                    color: task.soon ? tm.muted : tm.sub,
                                    lineHeight: 1.3, whiteSpace: 'pre-line'
                                }}>{task.label}</span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        {/* ── BIENTÔT DISPONIBLE ──────────────────────────────────────── */}
                        {studioTask === 'model3d-from-image' && (
                            <div className="text-center py-4">
                                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🧊</div>
                                <h6 className="fw-bold text-muted">Bientôt disponible</h6>
                                <p className="small text-muted mb-0">Cette fonctionnalité est en cours de développement.</p>
                            </div>
                        )}

                        {/* ── EDIT-VIDEO (sélection fichier) ──────────────────────────── */}
                        {studioTask === 'edit-video' && (
                            <div className="text-center py-4 px-3">
                                <div style={{ fontSize: '48px', marginBottom: '12px' }}>✂️</div>
                                <h6 className="fw-bold mb-1" style={{ color: tm.text }}>Éditer une vidéo</h6>
                                <p className="small mb-3" style={{ color: tm.muted }}>Sélectionnez une vidéo dans la liste de fichiers pour ouvrir l'éditeur.</p>
                                {selectablePromptFiles.filter(f => isVideo(f.name)).length > 0 ? (
                                    <div style={{ textAlign: 'left' }}>
                                        {selectablePromptFiles.filter(f => isVideo(f.name)).map(f => (
                                            <div key={f.url} onClick={() => { setPreviewFile(f); setIsEditingVideo(true); setShowAIPanel(false); }}
                                                className="d-flex align-items-center gap-2 p-2 rounded mb-1"
                                                style={{ cursor: 'pointer', background: tm.surface2, border: `1px solid ${tm.border}`, color: tm.text }}>
                                                <span>🎬</span>
                                                <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="small" style={{ color: tm.muted }}>Aucune vidéo disponible dans ce dossier.</p>
                                )}
                            </div>
                        )}

                        {/* ── EDIT-AUDIO ───────────────────────────────────────────────── */}
                        {studioTask === 'edit-audio' && (
                            <div className="text-center py-4 px-3">
                                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎧</div>
                                <h6 className="fw-bold mb-1" style={{ color: tm.text }}>Éditer un audio</h6>
                                <p className="small mb-3" style={{ color: tm.muted }}>Sélectionnez un fichier audio pour ouvrir l'éditeur.</p>
                                {selectablePromptFiles.filter(f => isAudio(f.name)).length > 0 ? (
                                    <div style={{ textAlign: 'left' }}>
                                        {selectablePromptFiles.filter(f => isAudio(f.name)).map(f => (
                                            <div key={f.url} onClick={() => { setPreviewFile(f); setIsEditingAudio(true); setShowAIPanel(false); setStudioTask(null); }}
                                                className="d-flex align-items-center gap-2 p-2 rounded mb-1"
                                                style={{ cursor: 'pointer', background: tm.surface2, border: `1px solid ${tm.border}`, color: tm.text }}>
                                                <span>🎵</span>
                                                <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="small" style={{ color: tm.muted }}>Aucun fichier audio disponible dans ce dossier.</p>
                                )}
                            </div>
                        )}

                        {/* ── TTS-VIDEO ────────────────────────────────────────────────── */}
                        {studioTask === 'tts-video' && (() => {
                            const videoSrc = ttsVideoFile || (previewFile && isVideo(previewFile.name) ? previewFile : null);
                            const videoFiles = selectablePromptFiles.filter(f => isVideo(f.name));
                            return (
                                <div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Vidéo source</label>
                                        {videoSrc ? (
                                            <div className="card border-success mb-2">
                                                <div className="card-body p-2 d-flex align-items-center gap-2">
                                                    <div className="rounded bg-dark d-flex align-items-center justify-content-center text-white flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                                                        <i className="fas fa-play" style={{ fontSize: '12px' }}></i>
                                                    </div>
                                                    <div className="small overflow-hidden flex-grow-1">
                                                        <div className="fw-bold text-truncate">{videoSrc.name}</div>
                                                        {ttsVideoFile && <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: '11px' }} onClick={() => setTtsVideoFile(null)}>Changer</button>}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="border rounded p-2" style={{ maxHeight: '130px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                                {videoFiles.length === 0
                                                    ? <div className="small" style={{ color: tm.muted }}>Aucune vidéo dans ce dossier.</div>
                                                    : videoFiles.map(f => (
                                                        <div key={f.url} className="d-flex align-items-center gap-2 p-1 rounded mb-1" style={{ cursor: 'pointer', color: tm.sub }} onClick={() => setTtsVideoFile(f)}>
                                                            <i className="fas fa-video" style={{ color: tm.muted }}></i>
                                                            <span className="small text-truncate">{f.name}</span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Texte à lire</label>
                                        <textarea className="form-control" rows={5} placeholder="Entrez le texte qui sera lu en voix off..." value={ttsText} onChange={e => setTtsText(e.target.value)} disabled={isRunningTts}></textarea>
                                    </div>
                                    <div className="row g-2 mb-3">
                                        <div className="col-6">
                                            <label className="form-label small fw-bold">Voix</label>
                                            <select className="form-select form-select-sm" value={ttsVoice} onChange={e => setTtsVoice(e.target.value)} disabled={isRunningTts}>
                                                {['alloy','echo','fable','onyx','nova','shimmer'].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small fw-bold">Qualité</label>
                                            <select className="form-select form-select-sm" value={ttsModelQ} onChange={e => setTtsModelQ(e.target.value)} disabled={isRunningTts}>
                                                <option value="tts-1">Standard</option>
                                                <option value="tts-1-hd">HD</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="d-grid">
                                        <button className="btn btn-warning text-dark" onClick={handleTtsVideo} disabled={isRunningTts || !videoSrc || !ttsText.trim()}>
                                            {isRunningTts ? <><span className="spinner-border spinner-border-sm me-2"></span>Génération...</> : <><i className="fas fa-volume-up me-2"></i>Ajouter la voix</>}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ── SUBTITLES ────────────────────────────────────────────────── */}
                        {studioTask === 'subtitles' && (() => {
                            const videoSrc = subVideoFile || (previewFile && isVideo(previewFile.name) ? previewFile : null);
                            const videoFiles = selectablePromptFiles.filter(f => isVideo(f.name));
                            return (
                                <div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Vidéo source</label>
                                        {videoSrc ? (
                                            <div className="card border-success mb-2">
                                                <div className="card-body p-2 d-flex align-items-center gap-2">
                                                    <div className="rounded bg-dark d-flex align-items-center justify-content-center text-white flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                                                        <i className="fas fa-play" style={{ fontSize: '12px' }}></i>
                                                    </div>
                                                    <div className="small overflow-hidden flex-grow-1">
                                                        <div className="fw-bold text-truncate">{videoSrc.name}</div>
                                                        {subVideoFile && <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: '11px' }} onClick={() => setSubVideoFile(null)}>Changer</button>}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="border rounded p-2" style={{ maxHeight: '130px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                                {videoFiles.length === 0
                                                    ? <div className="small" style={{ color: tm.muted }}>Aucune vidéo dans ce dossier.</div>
                                                    : videoFiles.map(f => (
                                                        <div key={f.url} className="d-flex align-items-center gap-2 p-1 rounded mb-1" style={{ cursor: 'pointer', color: tm.sub }} onClick={() => setSubVideoFile(f)}>
                                                            <i className="fas fa-video" style={{ color: tm.muted }}></i>
                                                            <span className="small text-truncate">{f.name}</span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        )}
                                    </div>
                                    <div className="row g-2 mb-3">
                                        <div className="col-7">
                                            <label className="form-label small fw-bold">Langue audio</label>
                                            <select className="form-select form-select-sm" value={subLanguage} onChange={e => setSubLanguage(e.target.value)} disabled={isRunningSubtitles}>
                                                <option value="auto">Détection auto</option>
                                                <option value="fr">Français</option>
                                                <option value="en">English</option>
                                                <option value="es">Español</option>
                                                <option value="de">Deutsch</option>
                                                <option value="it">Italiano</option>
                                                <option value="pt">Português</option>
                                                <option value="ar">العربية</option>
                                                <option value="zh">中文</option>
                                                <option value="ja">日本語</option>
                                            </select>
                                        </div>
                                        <div className="col-5">
                                            <label className="form-label small fw-bold">Résultat</label>
                                            <select className="form-select form-select-sm" value={subHardcode ? 'burn' : 'srt'} onChange={e => setSubHardcode(e.target.value === 'burn')} disabled={isRunningSubtitles}>
                                                <option value="burn">Incrustés</option>
                                                <option value="srt">Fichier SRT</option>
                                            </select>
                                        </div>
                                    </div>
                                    {subHardcode && (
                                        <div className="border rounded p-2 mb-3" style={{ background: tm.card, borderColor: tm.border }}>
                                            <div className="fw-bold small mb-2" style={{ color: tm.text }}>Style des sous-titres</div>
                                            <div className="row g-2 mb-2">
                                                <div className="col-8">
                                                    <label className="form-label small mb-1">Police</label>
                                                    <select className="form-select form-select-sm" value={subFont} onChange={e => setSubFont(e.target.value)} disabled={isRunningSubtitles}>
                                                        <option value="Arial">Arial</option>
                                                        <option value="Impact">Impact</option>
                                                        <option value="Helvetica">Helvetica</option>
                                                        <option value="Times New Roman">Times New Roman</option>
                                                        <option value="Courier New">Courier New</option>
                                                        <option value="Verdana">Verdana</option>
                                                        <option value="DejaVu Sans">DejaVu Sans</option>
                                                    </select>
                                                </div>
                                                <div className="col-4">
                                                    <label className="form-label small mb-1">Taille</label>
                                                    <input type="number" className="form-control form-control-sm" value={subFontSize} min={10} max={72} onChange={e => setSubFontSize(Number(e.target.value))} disabled={isRunningSubtitles} />
                                                </div>
                                            </div>
                                            <div className="row g-2 mb-2">
                                                <div className="col-6">
                                                    <label className="form-label small mb-1">Couleur texte</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input type="color" className="form-control form-control-color form-control-sm p-0" style={{ width: '36px', height: '30px', cursor: 'pointer' }} value={subColor} onChange={e => setSubColor(e.target.value)} disabled={isRunningSubtitles} />
                                                        <span className="small text-muted">{subColor.toUpperCase()}</span>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label small mb-1">Contour</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input type="color" className="form-control form-control-color form-control-sm p-0" style={{ width: '36px', height: '30px', cursor: 'pointer' }} value={subOutline} onChange={e => setSubOutline(e.target.value)} disabled={isRunningSubtitles} />
                                                        <span className="small text-muted">{subOutline.toUpperCase()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input className="form-check-input" type="checkbox" id="sub-bold" checked={subBold} onChange={e => setSubBold(e.target.checked)} disabled={isRunningSubtitles} />
                                                <label className="form-check-label small" htmlFor="sub-bold">Gras</label>
                                            </div>
                                        </div>
                                    )}
                                    <div className="d-grid mb-3">
                                        <button className="btn btn-indigo" style={{ backgroundColor: '#6366f1', color: 'white' }} onClick={handleSubtitles} disabled={isRunningSubtitles || !videoSrc}>
                                            {isRunningSubtitles ? <><span className="spinner-border spinner-border-sm me-2"></span>Transcription...</> : <><i className="fas fa-closed-captioning me-2"></i>Sous-titrer</>}
                                        </button>
                                    </div>
                                    {subtitleSrt && (
                                        <div>
                                            <label className="form-label small fw-bold">Aperçu SRT</label>
                                            <textarea className="form-control form-control-sm" rows={6} readOnly value={subtitleSrt} />
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ── OCR ─────────────────────────────────────────────────────── */}
                        {studioTask === 'ocr' && (
                            <div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Image source</label>
                                    {(ocrSourceFile || (previewFile && isImage(previewFile.name))) ? (
                                        <div className="card border-success mb-2">
                                            <div className="card-body p-2 d-flex align-items-center gap-2">
                                                <SafeImage
                                                    file={(ocrSourceFile || previewFile)!}
                                                    className="rounded"
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                />
                                                <div className="small overflow-hidden flex-grow-1">
                                                    <div className="fw-bold text-truncate">{(ocrSourceFile || previewFile)?.name}</div>
                                                    {ocrSourceFile && (
                                                        <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: '11px' }} onClick={() => setOcrSourceFile(null)}>Changer</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded p-2" style={{ maxHeight: '160px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                            {selectablePromptFiles.filter(f => isImage(f.name)).length === 0 ? (
                                                <div className="small" style={{ color: tm.muted }}>Aucune image dans ce dossier.</div>
                                            ) : selectablePromptFiles.filter(f => isImage(f.name)).map((file) => (
                                                <div
                                                    key={file.url}
                                                    className="d-flex align-items-center gap-2 p-1 rounded mb-1"
                                                    style={{ cursor: 'pointer', color: tm.sub }}
                                                    onClick={() => setOcrSourceFile(file)}
                                                >
                                                    <SafeImage file={file} className="rounded" style={{ width: '28px', height: '28px', objectFit: 'cover' }} />
                                                    <span className="small text-truncate">{file.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="d-grid mb-3">
                                    <button
                                        className="btn btn-success"
                                        onClick={handleOcr}
                                        disabled={isRunningOcr || !(ocrSourceFile || (previewFile && isImage(previewFile.name)))}
                                    >
                                        {isRunningOcr
                                            ? <><span className="spinner-border spinner-border-sm me-2"></span>Extraction...</>
                                            : <><i className="fas fa-file-alt me-2"></i>Extraire le texte</>}
                                    </button>
                                </div>
                                {ocrResult !== null && (
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label small fw-bold mb-0">Texte extrait</label>
                                            <button className="btn btn-sm btn-outline-secondary" onClick={handleDownloadOcrText}>
                                                <i className="fas fa-download me-1"></i>Télécharger
                                            </button>
                                        </div>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows={8}
                                            value={ocrResult}
                                            onChange={(e) => setOcrResult(e.target.value)}
                                            placeholder="Résultat OCR..."
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── IMAGE DEPUIS PROMPT ──────────────────────────────────────── */}
                        {studioTask === 'image-from-prompt' && (
                            <div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Modèle IA</label>
                                    {isLoadingModels ? (
                                        <div className="text-muted small">Chargement...</div>
                                    ) : availableImageModels.length === 0 ? (
                                        <div className="alert alert-warning py-2 small mb-0">Aucune clé IA pour la génération d'images.</div>
                                    ) : (
                                        <select className="form-select form-select-sm" value={selectedImageModel} onChange={(e) => setSelectedImageModel(e.target.value)} disabled={isGeneratingAI}>
                                            {availableImageModels.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.provider})</option>)}
                                        </select>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Description de l'image</label>
                                    <textarea className="form-control" rows={5} placeholder="Ex: Un chat astronaute sur la lune, style aquarelle..." value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={isGeneratingAI}></textarea>
                                </div>
                                {isGeneratingAI && aiProgress !== null && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1"><span>Génération...</span><span>{aiProgress}%</span></div>
                                        <div className="progress" style={{ height: '6px' }}><div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${aiProgress}%` }}></div></div>
                                    </div>
                                )}
                                <div className="d-grid">
                                    <button className="btn btn-primary" onClick={handleAIGenerate} disabled={isGeneratingAI || !aiPrompt.trim()}>
                                        {isGeneratingAI ? <><span className="spinner-border spinner-border-sm me-2"></span>Génération...</> : <><i className="fas fa-play-circle me-2"></i>Générer</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── VIDÉO DEPUIS PROMPT ─────────────────────────────────────── */}
                        {studioTask === 'video-from-prompt' && (
                            <div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Modèle IA</label>
                                    {isLoadingModels ? (
                                        <div className="text-muted small">Chargement...</div>
                                    ) : availableVideoModels.length === 0 ? (
                                        <div className="alert alert-warning py-2 small mb-0">Aucune clé IA pour la génération vidéo.</div>
                                    ) : (
                                        <select className="form-select form-select-sm" value={selectedVideoModel} onChange={(e) => setSelectedVideoModel(e.target.value)} disabled={isGeneratingAI}>
                                            {availableVideoModels.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.provider === 'LTX' ? 'LTX' : m.provider})</option>)}
                                        </select>
                                    )}
                                </div>
                                {selectedModel?.provider === 'LTX' && (
                                    <div className="border rounded p-2 mb-3" style={{ background: tm.card, borderColor: tm.border }}>
                                        <div className="fw-bold small mb-2" style={{ color: tm.text }}>Paramètres LTX</div>
                                        {ltxCreditsBlocked && <div className="alert alert-danger small py-2 mb-2">{ltxCreditsMessage || 'Crédits insuffisants.'}</div>}
                                        <div className="mb-2">
                                            <label className="form-label small mb-1">Preset</label>
                                            <select className="form-select form-select-sm" value={ltxPreset} onChange={(e) => setLtxPreset(e.target.value as 'auto' | 'custom')} disabled={isGeneratingAI || ltxCreditsBlocked}>
                                                <option value="auto">LTX auto</option>
                                                <option value="custom">Personnalisé</option>
                                            </select>
                                        </div>
                                        <div className="row g-2 mb-2">
                                            <div className="col-6">
                                                <label className="form-label small mb-1">Durée (s)</label>
                                                <select className="form-select form-select-sm" value={ltxDuration} onChange={(e) => setLtxDuration(Number(e.target.value))} disabled={isGeneratingAI || ltxPreset === 'auto' || ltxCreditsBlocked}>
                                                    {getLtxAllowedDurations(selectedModel.id, ltxResolution, ltxFps).map((v) => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label small mb-1">FPS</label>
                                                <select className="form-select form-select-sm" value={ltxFps} onChange={(e) => setLtxFps(Number(e.target.value))} disabled={isGeneratingAI || ltxPreset === 'auto' || ltxCreditsBlocked}>
                                                    {getLtxAllowedFps(selectedModel.id, ltxResolution).map((v) => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label small mb-1">Résolution</label>
                                                <select className="form-select form-select-sm" value={ltxResolution} onChange={(e) => setLtxResolution(e.target.value)} disabled={isGeneratingAI || ltxPreset === 'auto' || ltxCreditsBlocked}>
                                                    {getLtxAllowedResolutions(selectedModel.id).map((v) => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-check form-switch mb-2">
                                            <input className="form-check-input" type="checkbox" id="ltx-audio-vfp" checked={ltxGenerateAudio} onChange={(e) => setLtxGenerateAudio(e.target.checked)} disabled={isGeneratingAI || ltxCreditsBlocked} />
                                            <label className="form-check-label small" htmlFor="ltx-audio-vfp">Générer l'audio</label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="ltx-upload-vfp" checked={ltxUseUpload} onChange={(e) => setLtxUseUpload(e.target.checked)} disabled={isGeneratingAI || ltxCreditsBlocked} />
                                            <label className="form-check-label small" htmlFor="ltx-upload-vfp">Uploader les fichiers privés</label>
                                        </div>
                                    </div>
                                )}
                                {(isComfySelected || isFalAISelected) && renderComfyVideoSettings()}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Description de la vidéo</label>
                                    <textarea className="form-control" rows={4} placeholder="Ex: Un plan drone sur une ville cyberpunk, ambiance pluvieuse, 4k..." value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={isGeneratingAI}></textarea>
                                </div>
                                {isGeneratingAI && aiProgress !== null && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1"><span>Génération vidéo...</span><span>{aiProgress}%</span></div>
                                        <div className="progress" style={{ height: '6px' }}><div className="progress-bar progress-bar-striped progress-bar-animated bg-warning" role="progressbar" style={{ width: `${aiProgress}%` }}></div></div>
                                    </div>
                                )}
                                <div className="d-grid">
                                    <button className="btn btn-primary" onClick={handleAIGenerate} disabled={isGeneratingAI || !aiPrompt.trim() || (ltxCreditsBlocked && isLtxSelected)}>
                                        {isGeneratingAI ? <><span className="spinner-border spinner-border-sm me-2"></span>Génération...</> : <><i className="fas fa-play-circle me-2"></i>Générer</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── VIDÉO DEPUIS IMAGE ──────────────────────────────────────── */}
                        {studioTask === 'video-from-image' && (
                            <div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Image source</label>
                                    {selectedPromptFiles.filter(f => isImage(f.name)).length > 0 ? (
                                        <div className="card border-success mb-2">
                                            <div className="card-body p-2">
                                                {selectedPromptFiles.filter(f => isImage(f.name)).map(f => (
                                                    <div key={f.url} className="d-flex align-items-center gap-2">
                                                        <SafeImage file={f} className="rounded" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                        <div className="small text-truncate flex-grow-1">{f.name}</div>
                                                        <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => togglePromptFile(f)}>×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                            {selectablePromptFiles.filter(f => isImage(f.name)).length === 0 ? (
                                                <div className="small" style={{ color: tm.muted }}>Aucune image dans ce dossier.</div>
                                            ) : selectablePromptFiles.filter(f => isImage(f.name)).map((file) => (
                                                <div key={file.url} className="d-flex align-items-center gap-2 p-1 rounded mb-1" style={{ cursor: 'pointer', color: tm.sub }} onClick={() => togglePromptFile(file)}>
                                                    <SafeImage file={file} className="rounded" style={{ width: '28px', height: '28px', objectFit: 'cover' }} />
                                                    <span className="small text-truncate">{file.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Modèle IA</label>
                                    {isLoadingModels ? (
                                        <div className="text-muted small">Chargement...</div>
                                    ) : availableVideoModels.length === 0 ? (
                                        <div className="alert alert-warning py-2 small mb-0">Aucune clé IA pour la génération vidéo.</div>
                                    ) : (
                                        <select className="form-select form-select-sm" value={selectedVideoModel} onChange={(e) => setSelectedVideoModel(e.target.value)} disabled={isGeneratingAI}>
                                            {availableVideoModels.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.provider === 'LTX' ? 'LTX' : m.provider})</option>)}
                                        </select>
                                    )}
                                </div>
                                {selectedModel?.provider === 'LTX' && (
                                    <div className="border rounded p-2 mb-3" style={{ background: tm.card, borderColor: tm.border }}>
                                        <div className="fw-bold small mb-2" style={{ color: tm.text }}>Paramètres LTX</div>
                                        {ltxCreditsBlocked && <div className="alert alert-danger small py-2 mb-2">{ltxCreditsMessage || 'Crédits insuffisants.'}</div>}
                                        <div className="row g-2 mb-2">
                                            <div className="col-6">
                                                <label className="form-label small mb-1">Durée (s)</label>
                                                <select className="form-select form-select-sm" value={ltxDuration} onChange={(e) => setLtxDuration(Number(e.target.value))} disabled={isGeneratingAI || ltxPreset === 'auto' || ltxCreditsBlocked}>
                                                    {getLtxAllowedDurations(selectedModel.id, ltxResolution, ltxFps).map((v) => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label small mb-1">FPS</label>
                                                <select className="form-select form-select-sm" value={ltxFps} onChange={(e) => setLtxFps(Number(e.target.value))} disabled={isGeneratingAI || ltxPreset === 'auto' || ltxCreditsBlocked}>
                                                    {getLtxAllowedFps(selectedModel.id, ltxResolution).map((v) => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label small mb-1">Résolution</label>
                                                <select className="form-select form-select-sm" value={ltxResolution} onChange={(e) => setLtxResolution(e.target.value)} disabled={isGeneratingAI || ltxPreset === 'auto' || ltxCreditsBlocked}>
                                                    {getLtxAllowedResolutions(selectedModel.id).map((v) => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-check form-switch mb-2">
                                            <input className="form-check-input" type="checkbox" id="ltx-audio-vfi" checked={ltxGenerateAudio} onChange={(e) => setLtxGenerateAudio(e.target.checked)} disabled={isGeneratingAI || ltxCreditsBlocked} />
                                            <label className="form-check-label small" htmlFor="ltx-audio-vfi">Générer l'audio</label>
                                        </div>
                                    </div>
                                )}
                                {(isComfySelected || isFalAISelected) && renderComfyVideoSettings()}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">
                                        Prompt {(isComfySelected || isFalAISelected) ? '' : '(optionnel)'}
                                    </label>
                                    <textarea className="form-control" rows={3}
                                        placeholder={(isComfySelected || isFalAISelected)
                                            ? "Décrivez le mouvement, l'ambiance, la scène..."
                                            : "Ex: La scène prend vie avec du vent et des nuages..."}
                                        value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={isGeneratingAI} />
                                </div>
                                {isGeneratingAI && aiProgress !== null && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1"><span>Génération vidéo...</span><span>{aiProgress}%</span></div>
                                        <div className="progress" style={{ height: '6px' }}><div className="progress-bar progress-bar-striped progress-bar-animated bg-warning" role="progressbar" style={{ width: `${aiProgress}%` }}></div></div>
                                    </div>
                                )}
                                <div className="d-grid">
                                    <button className="btn btn-primary" onClick={handleAIGenerate}
                                        disabled={isGeneratingAI || selectedPromptFiles.filter(f => isImage(f.name)).length === 0 || (ltxCreditsBlocked && isLtxSelected) || ((isComfySelected || isFalAISelected) && !aiPrompt.trim())}>
                                        {isGeneratingAI ? <><span className="spinner-border spinner-border-sm me-2"></span>Génération...</> : <><i className="fas fa-play-circle me-2"></i>Générer</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── IMAGE + TEXTE → VIDÉO GPU (Wan2.1 I2V) ─────────────────── */}
                        {studioTask === 'video-img-comfy' && (
                            <div>
                                <div className="alert alert-info py-2 small mb-3">
                                    <i className="fas fa-magic me-1"></i>
                                    Génère une vidéo à partir d'une image de référence + prompt texte via <strong>Wan2.1 GPU {isFalAISelected ? '(fal.ai)' : '(GPU dédié)'}</strong>.
                                </div>
                                {/* Image source */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Image source</label>
                                    {selectedPromptFiles.filter(f => isImage(f.name)).length > 0 ? (
                                        <div className="card border-success mb-2">
                                            <div className="card-body p-2">
                                                {selectedPromptFiles.filter(f => isImage(f.name)).map(f => (
                                                    <div key={f.url} className="d-flex align-items-center gap-2">
                                                        <SafeImage file={f} className="rounded" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                        <div className="small text-truncate flex-grow-1">{f.name}</div>
                                                        <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => togglePromptFile(f)}>×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                            {selectablePromptFiles.filter(f => isImage(f.name)).length === 0 ? (
                                                <div className="small" style={{ color: tm.muted }}>Aucune image dans ce dossier.</div>
                                            ) : selectablePromptFiles.filter(f => isImage(f.name)).map((file) => (
                                                <div key={file.url} className="d-flex align-items-center gap-2 p-1 rounded mb-1" style={{ cursor: 'pointer', color: tm.sub }} onClick={() => togglePromptFile(file)}>
                                                    <SafeImage file={file} className="rounded" style={{ width: '28px', height: '28px', objectFit: 'cover' }} />
                                                    <span className="small text-truncate">{file.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {renderComfyVideoSettings()}
                                {/* Prompt */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Prompt de mouvement</label>
                                    <textarea className="form-control" rows={3}
                                        placeholder="Décrivez le mouvement, l'animation, l'ambiance..."
                                        value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={isGeneratingAI} />
                                </div>
                                {isGeneratingAI && aiProgress !== null && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1"><span>Génération vidéo GPU...</span><span>{aiProgress}%</span></div>
                                        <div className="progress" style={{ height: '6px' }}><div className="progress-bar progress-bar-striped progress-bar-animated bg-info" role="progressbar" style={{ width: `${aiProgress}%` }}></div></div>
                                    </div>
                                )}
                                <div className="d-grid">
                                    <button className="btn btn-primary" onClick={handleAIGenerate}
                                        disabled={isGeneratingAI || selectedPromptFiles.filter(f => isImage(f.name)).length === 0 || !aiPrompt.trim()}>
                                        {isGeneratingAI ? <><span className="spinner-border spinner-border-sm me-2"></span>Génération GPU...</> : <><i className="fas fa-play-circle me-2"></i>Générer (GPU {isFalAISelected ? 'fal.ai' : 'dédié'})</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── CONTINUER UNE VIDÉO ─────────────────────────────────────── */}
                        {studioTask === 'continue-video' && (
                            <div>
                                <div className="alert alert-info py-2 small mb-3">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Génère une suite à partir d'une vidéo existante. Nécessite le modèle <strong>LTX-2 Pro</strong>.
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Vidéo source</label>
                                    {selectedPromptFiles.filter(f => isVideo(f.name)).length > 0 ? (
                                        <div className="card border-success mb-2">
                                            <div className="card-body p-2">
                                                {selectedPromptFiles.filter(f => isVideo(f.name)).map(f => (
                                                    <div key={f.url} className="d-flex align-items-center gap-2">
                                                        <span style={{ fontSize: 24 }}>🎬</span>
                                                        <div className="small text-truncate flex-grow-1">{f.name}</div>
                                                        <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => togglePromptFile(f)}>×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                            {selectablePromptFiles.filter(f => isVideo(f.name)).length === 0 ? (
                                                <div className="small" style={{ color: tm.muted }}>Aucune vidéo dans ce dossier.</div>
                                            ) : selectablePromptFiles.filter(f => isVideo(f.name)).map((file) => (
                                                <div key={file.url} className="d-flex align-items-center gap-2 p-1 rounded mb-1" style={{ cursor: 'pointer', color: tm.sub }} onClick={() => togglePromptFile(file)}>
                                                    <span style={{ fontSize: 18 }}>🎬</span>
                                                    <span className="small text-truncate">{file.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Modèle IA</label>
                                    {isLoadingModels ? (
                                        <div className="text-muted small">Chargement...</div>
                                    ) : availableVideoModels.length === 0 ? (
                                        <div className="alert alert-warning py-2 small mb-0">Aucune clé IA pour la génération vidéo.</div>
                                    ) : (
                                        <select className="form-select form-select-sm" value={selectedVideoModel} onChange={(e) => setSelectedVideoModel(e.target.value)} disabled={isGeneratingAI}>
                                            {availableVideoModels.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.provider})</option>)}
                                        </select>
                                    )}
                                </div>
                                {isLtxSelected ? (
                                    <div className="border rounded p-2 mb-3" style={{ background: tm.card, borderColor: tm.border }}>
                                        <div className="fw-bold small mb-2" style={{ color: tm.text }}>Paramètres de continuation (LTX)</div>
                                        {ltxCreditsBlocked && <div className="alert alert-danger small py-2 mb-2">{ltxCreditsMessage || 'Crédits insuffisants.'}</div>}
                                        <div className="row g-2 mb-2">
                                            <div className="col-6">
                                                <label className="form-label small mb-1">Démarrer à (s)</label>
                                                <input type="number" className="form-control form-control-sm" min={0} step={1}
                                                    value={ltxRetakeStartTime}
                                                    onChange={(e) => setLtxRetakeStartTime(Math.max(0, Number(e.target.value)))}
                                                    disabled={isGeneratingAI || ltxCreditsBlocked}
                                                />
                                                <div className="form-text" style={{ fontSize: 10 }}>0 = depuis le début</div>
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label small mb-1">Durée à générer (s)</label>
                                                <select className="form-select form-select-sm" value={ltxRetakeDuration}
                                                    onChange={(e) => setLtxRetakeDuration(Number(e.target.value))}
                                                    disabled={isGeneratingAI || ltxCreditsBlocked}>
                                                    {[2, 4, 6, 8, 10, 12, 16, 20].map(v => <option key={v} value={v}>{v}s</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label small mb-1">Mode</label>
                                            <select className="form-select form-select-sm" value={ltxRetakeMode}
                                                onChange={(e) => setLtxRetakeMode(e.target.value)}
                                                disabled={isGeneratingAI || ltxCreditsBlocked}>
                                                <option value="replace_audio_and_video">Remplacer audio + vidéo</option>
                                                <option value="replace_video">Remplacer vidéo uniquement</option>
                                                <option value="replace_audio">Remplacer audio uniquement</option>
                                                <option value="extend">Étendre la vidéo (après la fin)</option>
                                            </select>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="ltx-audio-cv" checked={ltxGenerateAudio} onChange={(e) => setLtxGenerateAudio(e.target.checked)} disabled={isGeneratingAI || ltxCreditsBlocked} />
                                            <label className="form-check-label small" htmlFor="ltx-audio-cv">Générer l'audio</label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="alert alert-warning py-2 small mb-3">
                                        <i className="fas fa-exclamation-triangle me-1"></i>
                                        <strong>Veo 3.1 / Sora</strong> ne supportent pas la continuation native. La vidéo sera utilisée comme contexte mais le résultat sera une <em>nouvelle</em> vidéo. Pour une vraie continuation frame-à-frame, utilisez <strong>LTX-2 Pro</strong>.
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Description de la suite (prompt)</label>
                                    <textarea className="form-control" rows={3}
                                        placeholder="Ex: La caméra recule doucement, la lumière du soleil devient dorée, le personnage sourit..."
                                        value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={isGeneratingAI}>
                                    </textarea>
                                </div>
                                {isGeneratingAI && aiProgress !== null && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1"><span>Génération en cours...</span><span>{aiProgress}%</span></div>
                                        <div className="progress" style={{ height: '6px' }}><div className="progress-bar progress-bar-striped progress-bar-animated bg-warning" role="progressbar" style={{ width: `${aiProgress}%` }}></div></div>
                                    </div>
                                )}
                                <div className="d-grid">
                                    <button className="btn btn-warning text-white" onClick={handleAIGenerate}
                                        disabled={isGeneratingAI || selectedPromptFiles.filter(f => isVideo(f.name)).length === 0 || !aiPrompt.trim() || (ltxCreditsBlocked && isLtxSelected)}>
                                        {isGeneratingAI ? <><span className="spinner-border spinner-border-sm me-2"></span>Génération...</> : <><i className="fas fa-forward me-2"></i>Continuer la vidéo</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── ÉDITER UNE IMAGE ────────────────────────────────────────── */}
                        {studioTask === 'edit-image' && (
                            <div>
                                {previewFile && isImage(previewFile.name) ? (
                                    <div>
                                        <div className="alert alert-info small p-2 mb-3">
                                            <i className="fas fa-info-circle me-1"></i>
                                            Vous allez éditer : <strong>{previewFile.name}</strong>
                                        </div>
                                        <div className="d-grid">
                                            <button className="btn btn-primary" onClick={() => { setIsEditingImage(true); setShowAIPanel(false); setStudioTask(null); }}>
                                                <i className="fas fa-paint-brush me-2"></i>Ouvrir l'éditeur
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="form-label small fw-bold">Sélectionnez une image à éditer</label>
                                        <div className="border rounded p-2" style={{ maxHeight: '300px', overflowY: 'auto', background: tm.card, borderColor: tm.border }}>
                                            {selectablePromptFiles.filter(f => isImage(f.name)).length === 0 ? (
                                                <div className="small" style={{ color: tm.muted }}>Aucune image dans ce dossier.</div>
                                            ) : selectablePromptFiles.filter(f => isImage(f.name)).map((file) => (
                                                <div key={file.url} className="d-flex align-items-center gap-2 p-1 rounded mb-1">
                                                    <SafeImage file={file} className="rounded" style={{ width: '32px', height: '32px', objectFit: 'cover' }} />
                                                    <span className="small text-truncate flex-grow-1">{file.name}</span>
                                                    <button className="btn btn-sm btn-primary" onClick={() => { setPreviewFile(file); setIsEditingImage(true); setShowAIPanel(false); setStudioTask(null); }}>
                                                        Éditer
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        )}

        <div className="flex-grow-1 overflow-auto p-3" style={{ background: tm.bg }}>
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border" style={{ color: tm.accent }}></div>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center p-5" style={{ color: tm.muted }}>
              <i className="fas fa-folder-open fa-3x mb-3"></i>
              <p><AutoTranslateText sourceLang="fr">Dossier vide.</AutoTranslateText></p>
            </div>
          ) : (
            <>
            {viewMode === 'grid' && !previewFile && (
                <div className="row g-3">
                {files.map((file, index) => (
                    <div key={index} className="col-6 col-md-4 col-lg-3 mb-3">
                    <div
                        className="h-100 file-card position-relative"
                        onClick={() => {
                            if (file.isDir) handleNavigate(file.name);
                            else setPreviewFile(file);
                        }}
                        onDoubleClick={() => {
                            if (!file.isDir && onSelect) onSelect(getPublicSelectUrl(file.url));
                        }}
                        style={{
                            cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                            background: tm.card, border: `1px solid ${tm.border}`,
                            borderRadius: 10, overflow: 'hidden', boxShadow: tm.shadow,
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = darkMode ? '0 4px 16px rgba(0,0,0,.5)' : '0 4px 12px rgba(0,0,0,.12)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = tm.shadow; }}
                    >
                        <div className="position-absolute top-0 start-0 p-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isItemSelected(file)}
                            onChange={() => toggleItemSelection(file)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="text-center d-flex flex-column align-items-center justify-content-center p-2" style={{ color: tm.text }}>
                        {file.isDir ? (
                            <i className="fas fa-folder fa-3x text-warning mb-2"></i>
                        ) : (
                            isImage(file.name) ? (
                            <div style={{ height: '80px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {/* ✅ FIX: Utilisation de SafeImage pour gérer le fallback proxy S3 */}
                                <SafeImage 
                                    file={file} 
                                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
                                />
                            </div>
                            ) : (
                            <i className={`fas ${getFileIcon(file.name)} fa-3x mb-2`}></i>
                            )
                        )}
                        <div className="text-truncate w-100 mt-2" title={file.name} style={{ fontSize: 12, fontWeight: 500, color: tm.text }}>{file.name}</div>
                        </div>
                    </div>
                    </div>
                ))}
                </div>
            )}

            {(viewMode === 'list' || previewFile) && (
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead className="sticky-top" style={{ background: tm.surface, color: tm.sub }}>
                            <tr>
                                <th style={{width: '36px', background: tm.surface, color: tm.sub}} className="text-center">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={allSelected}
                                      onChange={(e) => toggleSelectAll(e.target.checked)}
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label="Tout sélectionner"
                                    />
                                </th>
                                <th style={{width: '40px', background: tm.surface}}></th>
                                <th style={{ background: tm.surface }}>Nom</th>
                                {!previewFile && <th style={{width: '100px', background: tm.surface}} className="d-none d-md-table-cell">Taille</th>}
                                {!previewFile && <th style={{width: '120px', background: tm.surface}} className="d-none d-md-table-cell">Date</th>}
                                <th style={{width: '80px', background: tm.surface}} className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {files.map((file, index) => (
                                <tr 
                                    key={index} 
                                    onClick={() => {
                                        if (file.isDir) handleNavigate(file.name);
                                        else setPreviewFile(file);
                                    }}
                                    onDoubleClick={() => {
                                        if (!file.isDir && onSelect) onSelect(getPublicSelectUrl(file.url));
                                    }}
                                    className={previewFile?.url === file.url ? 'table-active' : ''}
                                    style={{cursor: 'pointer'}}
                                >
                                    <td className="text-center">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={isItemSelected(file)}
                                          onChange={() => toggleItemSelection(file)}
                                          onClick={(e) => e.stopPropagation()}
                                          aria-label={`Sélectionner ${file.name}`}
                                        />
                                    </td>
                                    <td className="text-center">
                                        {file.isDir ? (
                                            <i className="fas fa-folder text-warning"></i>
                                        ) : (
                                            <i className={`fas ${getFileIcon(file.name)}`}></i>
                                        )}
                                    </td>
                                    <td>
                                        <span className="text-truncate d-inline-block" style={{maxWidth: previewFile ? '150px' : '250px'}}>
                                            {file.name}
                                        </span>
                                    </td>
                                    {!previewFile && <td className="small text-muted d-none d-md-table-cell">{formatSize(file.size)}</td>}
                                    {!previewFile && <td className="small text-muted d-none d-md-table-cell">{formatDate(file.lastModified)}</td>}
                                    <td className="text-end">
                                        <div className="btn-group btn-group-sm">
                                            <button 
                                                className="btn btn-link text-secondary p-1"
                                                onClick={(e) => handleRenamePrompt(file, e)}
                                                title="Renommer"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button 
                                                className="btn btn-link text-danger p-1"
                                                onClick={(e) => file.isDir ? handleDeleteFolder(file.name, e) : handleDelete(file, e)}
                                                title="Supprimer"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            </>
          )}
        </div>

        {previewFile && (
          <div
            className="p-3 overflow-auto"
            style={{
                width: isMobile ? '100%' : '350px',
                minWidth: isMobile ? '100%' : '300px',
                position: isMobile ? 'absolute' : 'relative',
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 20,
                background: tm.surface,
                borderLeft: `1px solid ${tm.border}`,
                color: tm.text,
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h5 className="text-truncate mb-0" title={previewFile.name} style={{ color: tm.text }}>Détails</h5>
              <button onClick={() => setPreviewFile(null)} aria-label="Fermer"
                style={{ background: 'none', border: `1px solid ${tm.border}`, borderRadius: 6, color: tm.muted, cursor: 'pointer', padding: '2px 8px' }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="mb-3" style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${tm.border}`, background: tm.card }}>
              <div className="text-center p-2 d-flex align-items-center justify-content-center" style={{ minHeight: '200px', background: tm.card }}>
                {isImage(previewFile.name) ? (
                  <div className="position-relative w-100 group-hover-container">
                    <SafeImage
                        key={previewFile.url}
                        file={previewFile}
                        className="img-fluid"
                        style={{ maxHeight: '250px' }}
                    />
                    <button 
                        className="btn btn-sm btn-light position-absolute top-0 end-0 m-2 shadow-sm"
                        onClick={() => setIsEditingImage(true)}
                        title="Éditer l'image"
                    >
                        <i className="fas fa-crop-alt"></i>
                    </button>
                  </div>
                ) : isPdf(previewFile.name) ? (
                   <div className="w-100">
                     <i className="fas fa-file-pdf fa-4x text-danger mb-3"></i>
                     <div className="ratio ratio-1x1 border rounded">
                        <iframe key={previewFile.url} src={`${previewFile.url}#toolbar=0&navpanes=0`} title="PDF Preview"></iframe>
                     </div>
                   </div>
                ) : isVideo(previewFile.name) ? (
                   <div className="w-100">
                     <video key={previewFile.url} controls className="w-100" style={{ maxHeight: '250px' }}>
                        <source src={previewFile.url} type={getVideoMimeType(previewFile.name)} />
                        Votre navigateur ne supporte pas la lecture de vidéos.
                     </video>
                     <div className="text-center mt-2">
                       <button className="btn btn-link btn-sm p-0 text-warning" onClick={() => setIsEditingVideo(true)}>
                         <i className="fas fa-film me-1"></i>Éditer la vidéo
                       </button>
                     </div>
                   </div>
                ) : isAudio(previewFile.name) ? (
                   <div className="w-100 p-2">
                     <i className="fas fa-music fa-4x text-info mb-3"></i>
                     <audio key={previewFile.url} controls className="w-100">
                        <source src={previewFile.url} type={getAudioMimeType(previewFile.name)} />
                        Votre navigateur ne supporte pas l'élément audio.
                     </audio>
                     <div className="text-center mt-2">
                       <button className="btn btn-link btn-sm p-0 text-info" onClick={() => setIsEditingAudio(true)}>
                         <i className="fas fa-sliders-h me-1"></i>Éditer l'audio
                       </button>
                     </div>
                   </div>
                ) : isFbx(previewFile.name) ? (
                    <FbxViewer key={previewFile.url} url={previewFile.url} />
                ) : isGlb(previewFile.name) ? (
                    <div key={previewFile.url} className="w-100" style={{ height: '250px' }}>
                        <model-viewer
                            src={previewFile.url}
                            alt={previewFile.name}
                            auto-rotate
                            camera-controls
                            style={{ width: '100%', height: '100%' }}
                        ></model-viewer>
                    </div>
                ) : (
                  <div>
                    <i className={`fas ${getFileIcon(previewFile.name)} fa-5x mb-3`}></i>
                    <div className="badge bg-secondary text-uppercase">{previewFile.name.split('.').pop()}</div>
                  </div>
                )}
              </div>
            </div>

            {isImage(previewFile.name) && (
                <div className="d-grid gap-2 mb-3">
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setIsEditingImage(true)}>
                        <i className="fas fa-pencil-alt me-2"></i>Éditer l'image
                    </button>
                </div>
            )}

            <div className="mb-4">
              <label className="form-label small" style={{ color: tm.muted }}>Nom du fichier</label>
              {isRenamingPreview ? (
                  <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        value={previewRenameValue}
                        onChange={(e) => setPreviewRenameValue(e.target.value)}
                        autoFocus
                        style={{ background: tm.input, color: tm.text, borderColor: tm.border }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitPreviewRename();
                            if (e.key === 'Escape') setIsRenamingPreview(false);
                        }}
                      />
                      <button className="btn btn-success" onClick={submitPreviewRename}><i className="fas fa-check"></i></button>
                      <button className="btn btn-outline-secondary" onClick={() => setIsRenamingPreview(false)}><i className="fas fa-times"></i></button>
                  </div>
              ) : (
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-bold text-break" style={{ color: tm.text }}>{previewFile.name}</div>
                    <button
                        style={{ background: 'none', border: 'none', color: tm.muted, cursor: 'pointer', padding: '0 4px' }}
                        onClick={() => {
                            setPreviewRenameValue(previewFile.name);
                            setIsRenamingPreview(true);
                        }}
                        title="Modifier le nom"
                    >
                        <i className="fas fa-pen"></i>
                    </button>
                  </div>
              )}
            </div>

            <div className="row mb-4">
              <div className="col-6">
                <label className="form-label small" style={{ color: tm.muted }}>Taille</label>
                <div className="fw-bold" style={{ color: tm.text }}>{formatSize(previewFile.size)}</div>
              </div>
              <div className="col-6">
                <label className="form-label small" style={{ color: tm.muted }}>Date</label>
                <div className="fw-bold" style={{ color: tm.text }}>{formatDate(previewFile.lastModified)}</div>
              </div>
            </div>

            <div className="d-grid gap-2">
              {onSelect && (
                <button style={{ background: tm.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', fontWeight: 600, cursor: 'pointer' }} onClick={() => onSelect(getPublicSelectUrl(previewFile.url))}>
                  <i className="fas fa-check me-2"></i> Sélectionner
                </button>
              )}
              <a href={previewFile.url} target="_blank" rel="noreferrer"
                style={{ display: 'block', textAlign: 'center', padding: '7px 0', borderRadius: 8, border: `1px solid ${tm.border}`, color: tm.sub, textDecoration: 'none', fontSize: 14 }}>
                <i className="fas fa-external-link-alt me-2"></i> Ouvrir / Télécharger
              </a>
              <button style={{ background: 'none', border: '1px solid #EF4444', borderRadius: 8, color: '#EF4444', padding: '7px 0', cursor: 'pointer', fontWeight: 500 }} onClick={() => handleDelete(previewFile)}>
                <i className="fas fa-trash me-2"></i> Supprimer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ✅ Si mode overlay, on centre le tout avec un fond sombre
  if (isOverlay) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999, // Juste en dessous de l'éditeur d'image (99999)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(3px)'
      }}>
        {content}
      </div>
    );
  }

  return content;
};

export default CloudFileManager;
